import { CommandSpec, FieldMeta, Key, Natural, URI, unwrapNatural, unwrapPositive, validNatural, validPositive } from './std';


import { ActionFeaturesJson, AttemptNumber, AttrPath, BanditCost, BanditProbability, Base64Body, Base64Nonce, Base64Signature, BigNatural, BranchName, BuildLog, ChosenIdx, ClosureSize, CommitSha, Cores, CpuModel, DerivationPath, DevSessionPhase, DispatchedAt, DrvHash, DurationMs, Ed25519Pubkey, EndedAt, EnvHostName, EnvSessionIdWire, ErrorOutput, EvalIdWire, ExitCode, FilePath, FlakeInputName, FlakeLockHash, GitRev, Group, GroupName, GroupTokenSecret, JobCount, JobIdWire, LastConnectedAt, LoadAverage, NarHash, NixFeatures, OutcomeAt, OutputTree, ProtocolVersion, RamBytes, RejectionReason, ReplayedAt, RetryBaseDelayMs, RetryCount, RetryMaxAttempts, RunnerName, SealedSecret, SessionSamplesJson, SharedFeaturesJson, StartedAt, StorePath, Substituters, TsUnixMs, Url, User, unwrapAttemptNumber, unwrapAttrPath, unwrapBanditProbability, unwrapBigNatural, unwrapBuildLog, unwrapChosenIdx, unwrapClosureSize, unwrapCommitSha, unwrapCores, unwrapDispatchedAt, unwrapDurationMs, unwrapEndedAt, unwrapEnvHostName, unwrapEnvSessionIdWire, unwrapErrorOutput, unwrapFlakeInputName, unwrapJobCount, unwrapLastConnectedAt, unwrapOutcomeAt, unwrapOutputTree, unwrapProbability, unwrapReplayedAt, unwrapRetryBaseDelayMs, unwrapRetryCount, unwrapRetryMaxAttempts, unwrapRunnerName, unwrapStartedAt, validBase64Nonce, validBase64Signature, validBigNatural, validDevSessionPhase, validEnvSessionIdWire, validEvalIdWire, validExitCode, validJobIdWire, validLoadAverage, validProbability, validProtocolVersion, validRamBytes, validSealedSecret } from './runtime';


import { Lane, Repo } from './repo';


declare const EnvDrainBrand: unique symbol;
export type EnvDrain = TsUnixMs & { readonly [EnvDrainBrand]: typeof EnvDrainBrand };
export function wrapEnvDrain(value: TsUnixMs): EnvDrain { return value as EnvDrain; }
export function unwrapEnvDrain(value: EnvDrain): TsUnixMs { return value; }

export const validEnvDrain = (x: TsUnixMs): EnvDrain => {
  return x as EnvDrain;
}

declare const TerminalDimensionBrand: unique symbol;
export type TerminalDimension = number & { readonly [TerminalDimensionBrand]: typeof TerminalDimensionBrand };
export function wrapTerminalDimension(value: number): TerminalDimension { return value as TerminalDimension; }
export function unwrapTerminalDimension(value: TerminalDimension): number { return value; }

declare const EnvRpcIdBrand: unique symbol;
export type EnvRpcId = Natural & { readonly [EnvRpcIdBrand]: typeof EnvRpcIdBrand };
export function wrapEnvRpcId(value: Natural): EnvRpcId { return value as EnvRpcId; }
export function unwrapEnvRpcId(value: EnvRpcId): Natural { return value; }

export const validEnvRpcId = (x: Natural): EnvRpcId => {
  return x as EnvRpcId;
}

declare const EnvFsWriteOkBrand: unique symbol;
export type EnvFsWriteOk = EnvRpcId & { readonly [EnvFsWriteOkBrand]: typeof EnvFsWriteOkBrand };
export function wrapEnvFsWriteOk(value: EnvRpcId): EnvFsWriteOk { return value as EnvFsWriteOk; }
export function unwrapEnvFsWriteOk(value: EnvFsWriteOk): EnvRpcId { return value; }

export const validEnvFsWriteOk = (x: EnvRpcId): EnvFsWriteOk => {
  return x as EnvFsWriteOk;
}

declare const SessionMemoryPercentBrand: unique symbol;
export type SessionMemoryPercent = number & { readonly [SessionMemoryPercentBrand]: typeof SessionMemoryPercentBrand };
export function wrapSessionMemoryPercent(value: number): SessionMemoryPercent { return value as SessionMemoryPercent; }
export function unwrapSessionMemoryPercent(value: SessionMemoryPercent): number { return value; }

declare const ServerUrlBrand: unique symbol;
export type ServerUrl = string & { readonly [ServerUrlBrand]: typeof ServerUrlBrand };
export function wrapServerUrl(value: string): ServerUrl { return value as ServerUrl; }
export function unwrapServerUrl(value: ServerUrl): string { return value; }

export const validServerUrl = (x: string): ServerUrl => {
  return x as ServerUrl;
}

declare const ServerPubkeyHexBrand: unique symbol;
export type ServerPubkeyHex = string & { readonly [ServerPubkeyHexBrand]: typeof ServerPubkeyHexBrand };
export function wrapServerPubkeyHex(value: string): ServerPubkeyHex { return value as ServerPubkeyHex; }
export function unwrapServerPubkeyHex(value: ServerPubkeyHex): string { return value; }

export const validServerPubkeyHex = (x: string): ServerPubkeyHex => {
  return x as ServerPubkeyHex;
}

declare const KeyFileBrand: unique symbol;
export type KeyFile = string & { readonly [KeyFileBrand]: typeof KeyFileBrand };
export function wrapKeyFile(value: string): KeyFile { return value as KeyFile; }
export function unwrapKeyFile(value: KeyFile): string { return value; }

export const validKeyFile = (x: string): KeyFile => {
  return x as KeyFile;
}

declare const GroupTokenFileBrand: unique symbol;
export type GroupTokenFile = string & { readonly [GroupTokenFileBrand]: typeof GroupTokenFileBrand };
export function wrapGroupTokenFile(value: string): GroupTokenFile { return value as GroupTokenFile; }
export function unwrapGroupTokenFile(value: GroupTokenFile): string { return value; }

export const validGroupTokenFile = (x: string): GroupTokenFile => {
  return x as GroupTokenFile;
}

declare const NixBinBrand: unique symbol;
export type NixBin = string & { readonly [NixBinBrand]: typeof NixBinBrand };
export function wrapNixBin(value: string): NixBin { return value as NixBin; }
export function unwrapNixBin(value: NixBin): string { return value; }

export const validNixBin = (x: string): NixBin => {
  return x as NixBin;
}

declare const SystemdRunBinBrand: unique symbol;
export type SystemdRunBin = string & { readonly [SystemdRunBinBrand]: typeof SystemdRunBinBrand };
export function wrapSystemdRunBin(value: string): SystemdRunBin { return value as SystemdRunBin; }
export function unwrapSystemdRunBin(value: SystemdRunBin): string { return value; }

export const validSystemdRunBin = (x: string): SystemdRunBin => {
  return x as SystemdRunBin;
}

declare const ScriptBinBrand: unique symbol;
export type ScriptBin = string & { readonly [ScriptBinBrand]: typeof ScriptBinBrand };
export function wrapScriptBin(value: string): ScriptBin { return value as ScriptBin; }
export function unwrapScriptBin(value: ScriptBin): string { return value; }

export const validScriptBin = (x: string): ScriptBin => {
  return x as ScriptBin;
}

declare const LoadIntervalMsBrand: unique symbol;
export type LoadIntervalMs = number & { readonly [LoadIntervalMsBrand]: typeof LoadIntervalMsBrand };
export function wrapLoadIntervalMs(value: number): LoadIntervalMs { return value as LoadIntervalMs; }
export function unwrapLoadIntervalMs(value: LoadIntervalMs): number { return value; }

declare const BackoffInitialMsBrand: unique symbol;
export type BackoffInitialMs = number & { readonly [BackoffInitialMsBrand]: typeof BackoffInitialMsBrand };
export function wrapBackoffInitialMs(value: number): BackoffInitialMs { return value as BackoffInitialMs; }
export function unwrapBackoffInitialMs(value: BackoffInitialMs): number { return value; }

declare const BackoffMaxMsBrand: unique symbol;
export type BackoffMaxMs = number & { readonly [BackoffMaxMsBrand]: typeof BackoffMaxMsBrand };
export function wrapBackoffMaxMs(value: number): BackoffMaxMs { return value as BackoffMaxMs; }
export function unwrapBackoffMaxMs(value: BackoffMaxMs): number { return value; }

declare const Base64SealedBrand: unique symbol;
export type Base64Sealed = string & { readonly [Base64SealedBrand]: typeof Base64SealedBrand };
export function wrapBase64Sealed(value: string): Base64Sealed { return value as Base64Sealed; }
export function unwrapBase64Sealed(value: Base64Sealed): string { return value; }

export const validBase64Sealed = (x: string): Base64Sealed => {
  return x as Base64Sealed;
}

declare const EvalSuccessBrand: unique symbol;
export type EvalSuccess = EvalLeaf[] & { readonly [EvalSuccessBrand]: typeof EvalSuccessBrand };
export function wrapEvalSuccess(value: EvalLeaf[]): EvalSuccess { return value as EvalSuccess; }
export function unwrapEvalSuccess(value: EvalSuccess): EvalLeaf[] { return value; }

export const validEvalSuccess = (x: EvalLeaf[]): EvalSuccess => {
  return x as EvalSuccess;
}

export interface EnvHost {
  group: Key<Group>;
  name: EnvHostName;
  pubkey: Ed25519Pubkey;
  arch: NixSystem;
  cores: Cores;
  ramBytes: RamBytes;
  cpuModel: CpuModel;
  nixFeatures: NixFeatures;
  substituters: Substituters;
  nixStoreMode: NixStoreMode | null;
  lastConnectedAt: LastConnectedAt | null;
  isOnline: boolean;
}

export const validEnvHost = (input: unknown): EnvHost => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["group"] === undefined) throw new Error('`_x["group"]` required but not present');
  
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  
  if (_x["pubkey"] === undefined) throw new Error('`_x["pubkey"]` required but not present');
  
  if (_x["arch"] === undefined) throw new Error('`_x["arch"]` required but not present');
  validNixSystem(_x["arch"] as any);
  if (_x["cores"] === undefined) throw new Error('`_x["cores"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["cores"] as any)) as any);
  if (_x["ramBytes"] === undefined) throw new Error('`_x["ramBytes"]` required but not present');
  validRamBytes(_x["ramBytes"] as any);
  if (_x["cpuModel"] === undefined) throw new Error('`_x["cpuModel"]` required but not present');
  
  if (_x["nixFeatures"] === undefined) throw new Error('`_x["nixFeatures"]` required but not present');
  
  if (_x["substituters"] === undefined) throw new Error('`_x["substituters"]` required but not present');
  
  if (_x["nixStoreMode"] === undefined) throw new Error('`_x["nixStoreMode"]` required but not present');
  _x["nixStoreMode"] === null || validNixStoreMode(_x["nixStoreMode"] as any);
  if (_x["lastConnectedAt"] === undefined) throw new Error('`_x["lastConnectedAt"]` required but not present');
  _x["lastConnectedAt"] === null || validNatural(unwrapNatural(unwrapLastConnectedAt(_x["lastConnectedAt"] as any)) as any);
  // handle html checkbox bs
  if(_x["isOnline"] === undefined){
    _x["isOnline"] = false;
  }else if((_x["isOnline"] as unknown) === 'on'){
    _x["isOnline"] = true;
  }
  if (_x["isOnline"] === undefined) throw new Error('`_x["isOnline"]` required but not present');
  if (typeof _x["isOnline"] != 'boolean') throw new Error('`_x["isOnline"]` must be a boolean');
  const x = input as EnvHost;
  return x;
}
export const isValidEnvHost = (input: unknown): boolean => {
  try { validEnvHost(input); return true; } catch { return false; }
}

export interface EnvHostSession {
  envHost: Key<EnvHost>;
  user: Key<User>;
  lane: Key<Lane>;
  branch: BranchName;
  sessionId: EnvSessionIdWire;
  state: DevSessionPhase;
  startedAt: StartedAt;
  lastAttachedAt: StartedAt | null;
  endedAt: EndedAt | null;
  chosenExplicitly: boolean;
  sessionPubkey: Ed25519Pubkey | null;
}

export const validEnvHostSession = (input: unknown): EnvHostSession => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["envHost"] === undefined) throw new Error('`_x["envHost"]` required but not present');
  
  if (_x["user"] === undefined) throw new Error('`_x["user"]` required but not present');
  
  if (_x["lane"] === undefined) throw new Error('`_x["lane"]` required but not present');
  
  if (_x["branch"] === undefined) throw new Error('`_x["branch"]` required but not present');
  
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["state"] === undefined) throw new Error('`_x["state"]` required but not present');
  validDevSessionPhase(_x["state"] as any);
  if (_x["startedAt"] === undefined) throw new Error('`_x["startedAt"]` required but not present');
  validBigNatural(unwrapBigNatural(unwrapStartedAt(_x["startedAt"] as any)) as any);
  if (_x["lastAttachedAt"] === undefined) throw new Error('`_x["lastAttachedAt"]` required but not present');
  _x["lastAttachedAt"] === null || validBigNatural(unwrapBigNatural(unwrapStartedAt(_x["lastAttachedAt"] as any)) as any);
  if (_x["endedAt"] === undefined) throw new Error('`_x["endedAt"]` required but not present');
  _x["endedAt"] === null || validBigNatural(unwrapBigNatural(unwrapEndedAt(_x["endedAt"] as any)) as any);
  // handle html checkbox bs
  if(_x["chosenExplicitly"] === undefined){
    _x["chosenExplicitly"] = false;
  }else if((_x["chosenExplicitly"] as unknown) === 'on'){
    _x["chosenExplicitly"] = true;
  }
  if (_x["chosenExplicitly"] === undefined) throw new Error('`_x["chosenExplicitly"]` required but not present');
  if (typeof _x["chosenExplicitly"] != 'boolean') throw new Error('`_x["chosenExplicitly"]` must be a boolean');
  if (_x["sessionPubkey"] === undefined) throw new Error('`_x["sessionPubkey"]` required but not present');
  
  const x = input as EnvHostSession;
  return x;
}
export const isValidEnvHostSession = (input: unknown): boolean => {
  try { validEnvHostSession(input); return true; } catch { return false; }
}

export interface SessionMetricsArchive {
  envHostSession: Key<EnvHostSession>;
  samplesJson: SessionSamplesJson;
  archivedAt: StartedAt;
}

export const validSessionMetricsArchive = (input: unknown): SessionMetricsArchive => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["envHostSession"] === undefined) throw new Error('`_x["envHostSession"]` required but not present');
  
  if (_x["samplesJson"] === undefined) throw new Error('`_x["samplesJson"]` required but not present');
  
  if (_x["archivedAt"] === undefined) throw new Error('`_x["archivedAt"]` required but not present');
  validBigNatural(unwrapBigNatural(unwrapStartedAt(_x["archivedAt"] as any)) as any);
  const x = input as SessionMetricsArchive;
  return x;
}
export const isValidSessionMetricsArchive = (input: unknown): boolean => {
  try { validSessionMetricsArchive(input); return true; } catch { return false; }
}

export interface EnvHostCapabilities {
  cores: Cores;
  ramBytes: RamBytes;
  arch: NixSystem;
  cpuModel: CpuModel;
  nixFeatures: NixFeatures;
  substituters: Substituters;
  nixStoreMode: NixStoreMode;
}

export const validEnvHostCapabilities = (input: unknown): EnvHostCapabilities => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["cores"] === undefined) throw new Error('`_x["cores"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["cores"] as any)) as any);
  if (_x["ramBytes"] === undefined) throw new Error('`_x["ramBytes"]` required but not present');
  validRamBytes(_x["ramBytes"] as any);
  if (_x["arch"] === undefined) throw new Error('`_x["arch"]` required but not present');
  validNixSystem(_x["arch"] as any);
  if (_x["cpuModel"] === undefined) throw new Error('`_x["cpuModel"]` required but not present');
  
  if (_x["nixFeatures"] === undefined) throw new Error('`_x["nixFeatures"]` required but not present');
  
  if (_x["substituters"] === undefined) throw new Error('`_x["substituters"]` required but not present');
  
  if (_x["nixStoreMode"] === undefined) throw new Error('`_x["nixStoreMode"]` required but not present');
  validNixStoreMode(_x["nixStoreMode"] as any);
  const x = input as EnvHostCapabilities;
  return x;
}
export const isValidEnvHostCapabilities = (input: unknown): boolean => {
  try { validEnvHostCapabilities(input); return true; } catch { return false; }
}

export interface EnvLoadSample {
  load1: LoadAverage;
  load5: LoadAverage;
  freeRamBytes: RamBytes;
  activeSessions: Cores;
}

export const validEnvLoadSample = (input: unknown): EnvLoadSample => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["load1"] === undefined) throw new Error('`_x["load1"]` required but not present');
  validLoadAverage(_x["load1"] as any);
  if (_x["load5"] === undefined) throw new Error('`_x["load5"]` required but not present');
  validLoadAverage(_x["load5"] as any);
  if (_x["freeRamBytes"] === undefined) throw new Error('`_x["freeRamBytes"]` required but not present');
  validRamBytes(_x["freeRamBytes"] as any);
  if (_x["activeSessions"] === undefined) throw new Error('`_x["activeSessions"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["activeSessions"] as any)) as any);
  const x = input as EnvLoadSample;
  return x;
}
export const isValidEnvLoadSample = (input: unknown): boolean => {
  try { validEnvLoadSample(input); return true; } catch { return false; }
}

export interface EnvSessionSample {
  sessionId: EnvSessionIdWire;
  cpuUsageUs: BigNatural;
  memBytes: RamBytes;
  tsMs: TsUnixMs;
}

export const validEnvSessionSample = (input: unknown): EnvSessionSample => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["cpuUsageUs"] === undefined) throw new Error('`_x["cpuUsageUs"]` required but not present');
  validBigNatural(_x["cpuUsageUs"] as any);
  if (_x["memBytes"] === undefined) throw new Error('`_x["memBytes"]` required but not present');
  validRamBytes(_x["memBytes"] as any);
  if (_x["tsMs"] === undefined) throw new Error('`_x["tsMs"]` required but not present');
  
  const x = input as EnvSessionSample;
  return x;
}
export const isValidEnvSessionSample = (input: unknown): boolean => {
  try { validEnvSessionSample(input); return true; } catch { return false; }
}

export interface EnvHello {
  groupName: GroupName;
  envHostName: EnvHostName;
  pubkey: Ed25519Pubkey;
}

export const validEnvHello = (input: unknown): EnvHello => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["groupName"] === undefined) throw new Error('`_x["groupName"]` required but not present');
  
  if (_x["envHostName"] === undefined) throw new Error('`_x["envHostName"]` required but not present');
  
  if (_x["pubkey"] === undefined) throw new Error('`_x["pubkey"]` required but not present');
  
  const x = input as EnvHello;
  return x;
}
export const isValidEnvHello = (input: unknown): boolean => {
  try { validEnvHello(input); return true; } catch { return false; }
}

export interface EnvStart {
  sessionId: EnvSessionIdWire;
  spec: EnvSpec;
}

export const validEnvStart = (input: unknown): EnvStart => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["spec"] === undefined) throw new Error('`_x["spec"]` required but not present');
  validEnvSpec(_x["spec"] as any);
  const x = input as EnvStart;
  return x;
}
export const isValidEnvStart = (input: unknown): boolean => {
  try { validEnvStart(input); return true; } catch { return false; }
}

export interface EnvCheckout {
  cloneUrl: string;
  ref: string;
  dir: string;
}

export const validEnvCheckout = (input: unknown): EnvCheckout => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["cloneUrl"] === undefined) throw new Error('`_x["cloneUrl"]` required but not present');
  if (typeof _x["cloneUrl"] != 'string') throw new Error('`_x["cloneUrl"]` must be a string');
  if (_x["ref"] === undefined) throw new Error('`_x["ref"]` required but not present');
  if (typeof _x["ref"] != 'string') throw new Error('`_x["ref"]` must be a string');
  if (_x["dir"] === undefined) throw new Error('`_x["dir"]` required but not present');
  if (typeof _x["dir"] != 'string') throw new Error('`_x["dir"]` must be a string');
  const x = input as EnvCheckout;
  return x;
}
export const isValidEnvCheckout = (input: unknown): boolean => {
  try { validEnvCheckout(input); return true; } catch { return false; }
}

export interface EnvSpec {
  flakeRef: string;
  derivationPath: DerivationPath;
  arch: NixSystem;
  expectedCores: Cores;
  expectedRamBytes: RamBytes;
  secrets: SealedSecret[];
  devshellAttrPath: string;
  cgroupMemoryMaxBytes: RamBytes | null;
  cgroupCpuWeight: Natural | null;
  cgroupIoWeight: Natural | null;
  checkout: EnvCheckout | null;
}

export const validEnvSpec = (input: unknown): EnvSpec => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["flakeRef"] === undefined) throw new Error('`_x["flakeRef"]` required but not present');
  if (typeof _x["flakeRef"] != 'string') throw new Error('`_x["flakeRef"]` must be a string');
  if (_x["derivationPath"] === undefined) throw new Error('`_x["derivationPath"]` required but not present');
  
  if (_x["arch"] === undefined) throw new Error('`_x["arch"]` required but not present');
  validNixSystem(_x["arch"] as any);
  if (_x["expectedCores"] === undefined) throw new Error('`_x["expectedCores"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["expectedCores"] as any)) as any);
  if (_x["expectedRamBytes"] === undefined) throw new Error('`_x["expectedRamBytes"]` required but not present');
  validRamBytes(_x["expectedRamBytes"] as any);
  if (_x["secrets"] === undefined) throw new Error('`_x["secrets"]` required but not present');
    if(_x["secrets"] instanceof Array){
    if((_x["secrets"] as any)?.length){
      (_x["secrets"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validSealedSecret(y as any);
      });
    }else{
      _x["secrets"] = _x["secrets"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["secrets"]` must be an Array');
  }
  if (_x["devshellAttrPath"] === undefined) throw new Error('`_x["devshellAttrPath"]` required but not present');
  if (typeof _x["devshellAttrPath"] != 'string') throw new Error('`_x["devshellAttrPath"]` must be a string');
  if (_x["cgroupMemoryMaxBytes"] === undefined) throw new Error('`_x["cgroupMemoryMaxBytes"]` required but not present');
  _x["cgroupMemoryMaxBytes"] === null || validRamBytes(_x["cgroupMemoryMaxBytes"] as any);
  if (_x["cgroupCpuWeight"] === undefined) throw new Error('`_x["cgroupCpuWeight"]` required but not present');
  _x["cgroupCpuWeight"] === null || validNatural(_x["cgroupCpuWeight"] as any);
  if (_x["cgroupIoWeight"] === undefined) throw new Error('`_x["cgroupIoWeight"]` required but not present');
  _x["cgroupIoWeight"] === null || validNatural(_x["cgroupIoWeight"] as any);
  if (_x["checkout"] === undefined) throw new Error('`_x["checkout"]` required but not present');
  _x["checkout"] === null || validEnvCheckout(_x["checkout"] as any);
  const x = input as EnvSpec;
  return x;
}
export const isValidEnvSpec = (input: unknown): boolean => {
  try { validEnvSpec(input); return true; } catch { return false; }
}

export interface EnvStateChanged {
  sessionId: EnvSessionIdWire;
  phase: DevSessionPhase;
}

export const validEnvStateChanged = (input: unknown): EnvStateChanged => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["phase"] === undefined) throw new Error('`_x["phase"]` required but not present');
  validDevSessionPhase(_x["phase"] as any);
  const x = input as EnvStateChanged;
  return x;
}
export const isValidEnvStateChanged = (input: unknown): boolean => {
  try { validEnvStateChanged(input); return true; } catch { return false; }
}

export interface EnvExit {
  sessionId: EnvSessionIdWire;
  exitCode: ExitCode;
  peakRssBytes: RamBytes | null;
}

export const validEnvExit = (input: unknown): EnvExit => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["exitCode"] === undefined) throw new Error('`_x["exitCode"]` required but not present');
  validExitCode(_x["exitCode"] as any);
  if (_x["peakRssBytes"] === undefined) throw new Error('`_x["peakRssBytes"]` required but not present');
  _x["peakRssBytes"] === null || validRamBytes(_x["peakRssBytes"] as any);
  const x = input as EnvExit;
  return x;
}
export const isValidEnvExit = (input: unknown): boolean => {
  try { validEnvExit(input); return true; } catch { return false; }
}

export interface EnvRejected {
  sessionId: EnvSessionIdWire;
  reason: RejectionReason;
}

export const validEnvRejected = (input: unknown): EnvRejected => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["reason"] === undefined) throw new Error('`_x["reason"]` required but not present');
  
  const x = input as EnvRejected;
  return x;
}
export const isValidEnvRejected = (input: unknown): boolean => {
  try { validEnvRejected(input); return true; } catch { return false; }
}

export interface EnvAttachRejected {
  sessionId: EnvSessionIdWire;
  reason: RejectionReason;
}

export const validEnvAttachRejected = (input: unknown): EnvAttachRejected => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["reason"] === undefined) throw new Error('`_x["reason"]` required but not present');
  
  const x = input as EnvAttachRejected;
  return x;
}
export const isValidEnvAttachRejected = (input: unknown): boolean => {
  try { validEnvAttachRejected(input); return true; } catch { return false; }
}

export interface EnvResize {
  sessionId: EnvSessionIdWire;
  cols: TerminalDimension;
  rows: TerminalDimension;
}

export const validEnvResize = (input: unknown): EnvResize => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["cols"] === undefined) throw new Error('`_x["cols"]` required but not present');
  validTerminalDimension(_x["cols"] as any);
  if (_x["rows"] === undefined) throw new Error('`_x["rows"]` required but not present');
  validTerminalDimension(_x["rows"] as any);
  const x = input as EnvResize;
  return x;
}
export const isValidEnvResize = (input: unknown): boolean => {
  try { validEnvResize(input); return true; } catch { return false; }
}

export interface EnvFsWrite {
  rpcId: EnvRpcId;
  sessionId: EnvSessionIdWire;
  path: FilePath;
  contentsB64: Base64Body;
}

export const validEnvFsWrite = (input: unknown): EnvFsWrite => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["path"] === undefined) throw new Error('`_x["path"]` required but not present');
  
  if (_x["contentsB64"] === undefined) throw new Error('`_x["contentsB64"]` required but not present');
  
  const x = input as EnvFsWrite;
  return x;
}
export const isValidEnvFsWrite = (input: unknown): boolean => {
  try { validEnvFsWrite(input); return true; } catch { return false; }
}

export interface EnvFsRead {
  rpcId: EnvRpcId;
  sessionId: EnvSessionIdWire;
  path: FilePath;
}

export const validEnvFsRead = (input: unknown): EnvFsRead => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["path"] === undefined) throw new Error('`_x["path"]` required but not present');
  
  const x = input as EnvFsRead;
  return x;
}
export const isValidEnvFsRead = (input: unknown): boolean => {
  try { validEnvFsRead(input); return true; } catch { return false; }
}

export interface EnvGitCommit {
  rpcId: EnvRpcId;
  sessionId: EnvSessionIdWire;
  message: string;
  authorName: string;
  authorEmail: string;
}

export const validEnvGitCommit = (input: unknown): EnvGitCommit => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["sessionId"] === undefined) throw new Error('`_x["sessionId"]` required but not present');
  
  if (_x["message"] === undefined) throw new Error('`_x["message"]` required but not present');
  if (typeof _x["message"] != 'string') throw new Error('`_x["message"]` must be a string');
  if (_x["authorName"] === undefined) throw new Error('`_x["authorName"]` required but not present');
  if (typeof _x["authorName"] != 'string') throw new Error('`_x["authorName"]` must be a string');
  if (_x["authorEmail"] === undefined) throw new Error('`_x["authorEmail"]` required but not present');
  if (typeof _x["authorEmail"] != 'string') throw new Error('`_x["authorEmail"]` must be a string');
  const x = input as EnvGitCommit;
  return x;
}
export const isValidEnvGitCommit = (input: unknown): boolean => {
  try { validEnvGitCommit(input); return true; } catch { return false; }
}

export interface EnvFsReadOk {
  rpcId: EnvRpcId;
  contentsB64: Base64Body;
}

export const validEnvFsReadOk = (input: unknown): EnvFsReadOk => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["contentsB64"] === undefined) throw new Error('`_x["contentsB64"]` required but not present');
  
  const x = input as EnvFsReadOk;
  return x;
}
export const isValidEnvFsReadOk = (input: unknown): boolean => {
  try { validEnvFsReadOk(input); return true; } catch { return false; }
}

export interface EnvGitCommitOk {
  rpcId: EnvRpcId;
  sha: CommitSha;
}

export const validEnvGitCommitOk = (input: unknown): EnvGitCommitOk => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["sha"] === undefined) throw new Error('`_x["sha"]` required but not present');
  
  const x = input as EnvGitCommitOk;
  return x;
}
export const isValidEnvGitCommitOk = (input: unknown): boolean => {
  try { validEnvGitCommitOk(input); return true; } catch { return false; }
}

export interface EnvRpcFailed {
  rpcId: EnvRpcId;
  reason: string;
}

export const validEnvRpcFailed = (input: unknown): EnvRpcFailed => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["reason"] === undefined) throw new Error('`_x["reason"]` required but not present');
  if (typeof _x["reason"] != 'string') throw new Error('`_x["reason"]` must be a string');
  const x = input as EnvRpcFailed;
  return x;
}
export const isValidEnvRpcFailed = (input: unknown): boolean => {
  try { validEnvRpcFailed(input); return true; } catch { return false; }
}

export interface EnvListDevShells {
  rpcId: EnvRpcId;
  flakeRef: string;
  secrets: SealedSecret[];
}

export const validEnvListDevShells = (input: unknown): EnvListDevShells => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["flakeRef"] === undefined) throw new Error('`_x["flakeRef"]` required but not present');
  if (typeof _x["flakeRef"] != 'string') throw new Error('`_x["flakeRef"]` must be a string');
  if (_x["secrets"] === undefined) throw new Error('`_x["secrets"]` required but not present');
    if(_x["secrets"] instanceof Array){
    if((_x["secrets"] as any)?.length){
      (_x["secrets"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validSealedSecret(y as any);
      });
    }else{
      _x["secrets"] = _x["secrets"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["secrets"]` must be an Array');
  }
  const x = input as EnvListDevShells;
  return x;
}
export const isValidEnvListDevShells = (input: unknown): boolean => {
  try { validEnvListDevShells(input); return true; } catch { return false; }
}

export interface EnvDevShell {
  system: string;
  name: string;
}

export const validEnvDevShell = (input: unknown): EnvDevShell => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["system"] === undefined) throw new Error('`_x["system"]` required but not present');
  if (typeof _x["system"] != 'string') throw new Error('`_x["system"]` must be a string');
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  if (typeof _x["name"] != 'string') throw new Error('`_x["name"]` must be a string');
  const x = input as EnvDevShell;
  return x;
}
export const isValidEnvDevShell = (input: unknown): boolean => {
  try { validEnvDevShell(input); return true; } catch { return false; }
}

export interface EnvDevShellsOk {
  rpcId: EnvRpcId;
  shells: EnvDevShell[];
}

export const validEnvDevShellsOk = (input: unknown): EnvDevShellsOk => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["rpcId"] === undefined) throw new Error('`_x["rpcId"]` required but not present');
  validNatural(unwrapNatural(unwrapEnvRpcId(_x["rpcId"] as any)) as any);
  if (_x["shells"] === undefined) throw new Error('`_x["shells"]` required but not present');
    if(_x["shells"] instanceof Array){
    if((_x["shells"] as any)?.length){
      (_x["shells"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validEnvDevShell(y as any);
      });
    }else{
      _x["shells"] = _x["shells"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["shells"]` must be an Array');
  }
  const x = input as EnvDevShellsOk;
  return x;
}
export const isValidEnvDevShellsOk = (input: unknown): boolean => {
  try { validEnvDevShellsOk(input); return true; } catch { return false; }
}

export type EnvFsEventKind = "created" | "modified" | "deleted" | "moved"

export const validEnvFsEventKind = (x: unknown): EnvFsEventKind => {
  const v = x as [string, unknown] | string;
  if(((v != 'created')) && ((v != 'modified')) && ((v != 'deleted')) && ((v != 'moved'))) throw new Error('Invalid EnvFsEventKind `' + JSON.stringify(x) + '`');
  return x as EnvFsEventKind;
}

export interface EnvFsEvent {
  kind: EnvFsEventKind;
  path: FilePath;
  fromPath: FilePath | null;
}

export const validEnvFsEvent = (input: unknown): EnvFsEvent => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["kind"] === undefined) throw new Error('`_x["kind"]` required but not present');
  validEnvFsEventKind(_x["kind"] as any);
  if (_x["path"] === undefined) throw new Error('`_x["path"]` required but not present');
  
  if (_x["fromPath"] === undefined) throw new Error('`_x["fromPath"]` required but not present');
  
  const x = input as EnvFsEvent;
  return x;
}
export const isValidEnvFsEvent = (input: unknown): boolean => {
  try { validEnvFsEvent(input); return true; } catch { return false; }
}

export type ServerToEnvHostMsg = [ "challenge", Base64Nonce ] | [ "envStart", EnvStart ] | [ "envCancel", EnvSessionIdWire ] | [ "envAttach", EnvSessionIdWire ] | [ "envResize", EnvResize ] | [ "envDrain", EnvDrain ] | [ "envListDevShells", EnvListDevShells ] | [ "envFsWrite", EnvFsWrite ] | [ "envFsRead", EnvFsRead ] | [ "envGitCommit", EnvGitCommit ] | "ping"

export const validServerToEnvHostMsg = (x: unknown): ServerToEnvHostMsg => {
  const v = x as [string, unknown] | string;
  if((!(v[0] === 'challenge' && validBase64Nonce(v[1] as Base64Nonce)) && (v[0] != undefined && v[0] != 'challenge')) && (!(v[0] === 'envStart' && validEnvStart(v[1] as EnvStart)) && (v[0] != undefined && v[0] != 'envStart')) && (!(v[0] === 'envCancel' && validEnvSessionIdWire(v[1] as EnvSessionIdWire)) && (v[0] != undefined && v[0] != 'envCancel')) && (!(v[0] === 'envAttach' && validEnvSessionIdWire(v[1] as EnvSessionIdWire)) && (v[0] != undefined && v[0] != 'envAttach')) && (!(v[0] === 'envResize' && validEnvResize(v[1] as EnvResize)) && (v[0] != undefined && v[0] != 'envResize')) && (!(v[0] === 'envDrain' && validEnvDrain(v[1] as EnvDrain)) && (v[0] != undefined && v[0] != 'envDrain')) && (!(v[0] === 'envListDevShells' && validEnvListDevShells(v[1] as EnvListDevShells)) && (v[0] != undefined && v[0] != 'envListDevShells')) && (!(v[0] === 'envFsWrite' && validEnvFsWrite(v[1] as EnvFsWrite)) && (v[0] != undefined && v[0] != 'envFsWrite')) && (!(v[0] === 'envFsRead' && validEnvFsRead(v[1] as EnvFsRead)) && (v[0] != undefined && v[0] != 'envFsRead')) && (!(v[0] === 'envGitCommit' && validEnvGitCommit(v[1] as EnvGitCommit)) && (v[0] != undefined && v[0] != 'envGitCommit')) && ((v != 'ping'))) throw new Error('Invalid ServerToEnvHostMsg `' + JSON.stringify(x) + '`');
  return x as ServerToEnvHostMsg;
}

export type EnvHostToServerMsg = [ "hello", EnvHello ] | [ "auth", Base64Signature ] | [ "capabilities", EnvHostCapabilities ] | [ "load", EnvLoadSample ] | [ "sessionSample", EnvSessionSample ] | [ "envAccepted", EnvSessionIdWire ] | [ "envRejected", EnvRejected ] | [ "envAttachRejected", EnvAttachRejected ] | [ "envStateChanged", EnvStateChanged ] | [ "envExit", EnvExit ] | [ "envDevShellsOk", EnvDevShellsOk ] | [ "envFsWriteOk", EnvFsWriteOk ] | [ "envFsReadOk", EnvFsReadOk ] | [ "envGitCommitOk", EnvGitCommitOk ] | [ "envRpcFailed", EnvRpcFailed ] | "pong"

export const validEnvHostToServerMsg = (x: unknown): EnvHostToServerMsg => {
  const v = x as [string, unknown] | string;
  if((!(v[0] === 'hello' && validEnvHello(v[1] as EnvHello)) && (v[0] != undefined && v[0] != 'hello')) && (!(v[0] === 'auth' && validBase64Signature(v[1] as Base64Signature)) && (v[0] != undefined && v[0] != 'auth')) && (!(v[0] === 'capabilities' && validEnvHostCapabilities(v[1] as EnvHostCapabilities)) && (v[0] != undefined && v[0] != 'capabilities')) && (!(v[0] === 'load' && validEnvLoadSample(v[1] as EnvLoadSample)) && (v[0] != undefined && v[0] != 'load')) && (!(v[0] === 'sessionSample' && validEnvSessionSample(v[1] as EnvSessionSample)) && (v[0] != undefined && v[0] != 'sessionSample')) && (!(v[0] === 'envAccepted' && validEnvSessionIdWire(v[1] as EnvSessionIdWire)) && (v[0] != undefined && v[0] != 'envAccepted')) && (!(v[0] === 'envRejected' && validEnvRejected(v[1] as EnvRejected)) && (v[0] != undefined && v[0] != 'envRejected')) && (!(v[0] === 'envAttachRejected' && validEnvAttachRejected(v[1] as EnvAttachRejected)) && (v[0] != undefined && v[0] != 'envAttachRejected')) && (!(v[0] === 'envStateChanged' && validEnvStateChanged(v[1] as EnvStateChanged)) && (v[0] != undefined && v[0] != 'envStateChanged')) && (!(v[0] === 'envExit' && validEnvExit(v[1] as EnvExit)) && (v[0] != undefined && v[0] != 'envExit')) && (!(v[0] === 'envDevShellsOk' && validEnvDevShellsOk(v[1] as EnvDevShellsOk)) && (v[0] != undefined && v[0] != 'envDevShellsOk')) && (!(v[0] === 'envFsWriteOk' && validEnvFsWriteOk(v[1] as EnvFsWriteOk)) && (v[0] != undefined && v[0] != 'envFsWriteOk')) && (!(v[0] === 'envFsReadOk' && validEnvFsReadOk(v[1] as EnvFsReadOk)) && (v[0] != undefined && v[0] != 'envFsReadOk')) && (!(v[0] === 'envGitCommitOk' && validEnvGitCommitOk(v[1] as EnvGitCommitOk)) && (v[0] != undefined && v[0] != 'envGitCommitOk')) && (!(v[0] === 'envRpcFailed' && validEnvRpcFailed(v[1] as EnvRpcFailed)) && (v[0] != undefined && v[0] != 'envRpcFailed')) && ((v != 'pong'))) throw new Error('Invalid EnvHostToServerMsg `' + JSON.stringify(x) + '`');
  return x as EnvHostToServerMsg;
}

export interface EnvRegisterPayload {
  protocolVersion: ProtocolVersion;
  groupId: GroupTokenSecret;
  groupName: GroupName;
  envHostName: EnvHostName;
  envHostPubkey: Ed25519Pubkey;
  capabilities: EnvHostCapabilities;
  tsMs: TsUnixMs;
  nonce: Base64Nonce;
  selfSig: Base64Signature;
}

export const validEnvRegisterPayload = (input: unknown): EnvRegisterPayload => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["protocolVersion"] === undefined) throw new Error('`_x["protocolVersion"]` required but not present');
  validProtocolVersion(_x["protocolVersion"] as any);
  if (_x["groupId"] === undefined) throw new Error('`_x["groupId"]` required but not present');
  
  if (_x["groupName"] === undefined) throw new Error('`_x["groupName"]` required but not present');
  
  if (_x["envHostName"] === undefined) throw new Error('`_x["envHostName"]` required but not present');
  
  if (_x["envHostPubkey"] === undefined) throw new Error('`_x["envHostPubkey"]` required but not present');
  
  if (_x["capabilities"] === undefined) throw new Error('`_x["capabilities"]` required but not present');
  validEnvHostCapabilities(_x["capabilities"] as any);
  if (_x["tsMs"] === undefined) throw new Error('`_x["tsMs"]` required but not present');
  
  if (_x["nonce"] === undefined) throw new Error('`_x["nonce"]` required but not present');
  
  if (_x["selfSig"] === undefined) throw new Error('`_x["selfSig"]` required but not present');
  
  const x = input as EnvRegisterPayload;
  return x;
}
export const isValidEnvRegisterPayload = (input: unknown): boolean => {
  try { validEnvRegisterPayload(input); return true; } catch { return false; }
}

export interface EnvHostRegisterRequest {
  sealed: Base64Sealed;
  protocolVersion: ProtocolVersion;
}

export const validEnvHostRegisterRequest = (input: unknown): EnvHostRegisterRequest => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sealed"] === undefined) throw new Error('`_x["sealed"]` required but not present');
  
  if (_x["protocolVersion"] === undefined) throw new Error('`_x["protocolVersion"]` required but not present');
  validProtocolVersion(_x["protocolVersion"] as any);
  const x = input as EnvHostRegisterRequest;
  return x;
}
export const isValidEnvHostRegisterRequest = (input: unknown): boolean => {
  try { validEnvHostRegisterRequest(input); return true; } catch { return false; }
}

export interface WorkspaceTemplate {
  name: string;
  group: Key<Group>;
  repo: Key<Repo>;
  lane: Key<Lane>;
  flakeRef: string;
  devshellAttrPath: string;
  lockRev: string;
  preferredEnvHost: Key<EnvHost> | null;
  description: string | null;
}

export const validWorkspaceTemplate = (input: unknown): WorkspaceTemplate => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  if (typeof _x["name"] != 'string') throw new Error('`_x["name"]` must be a string');
  if (_x["group"] === undefined) throw new Error('`_x["group"]` required but not present');
  
  if (_x["repo"] === undefined) throw new Error('`_x["repo"]` required but not present');
  
  if (_x["lane"] === undefined) throw new Error('`_x["lane"]` required but not present');
  
  if (_x["flakeRef"] === undefined) throw new Error('`_x["flakeRef"]` required but not present');
  if (typeof _x["flakeRef"] != 'string') throw new Error('`_x["flakeRef"]` must be a string');
  if (_x["devshellAttrPath"] === undefined) throw new Error('`_x["devshellAttrPath"]` required but not present');
  if (typeof _x["devshellAttrPath"] != 'string') throw new Error('`_x["devshellAttrPath"]` must be a string');
  if (_x["lockRev"] === undefined) throw new Error('`_x["lockRev"]` required but not present');
  if (typeof _x["lockRev"] != 'string') throw new Error('`_x["lockRev"]` must be a string');
  if (_x["preferredEnvHost"] === undefined) throw new Error('`_x["preferredEnvHost"]` required but not present');
  
  if (_x["description"] === undefined) throw new Error('`_x["description"]` required but not present');
  if (typeof _x["description"] != 'string' && _x["description"] != null) throw new Error('`_x["description"]` must be a string | null');
  const x = input as WorkspaceTemplate;
  return x;
}
export const isValidWorkspaceTemplate = (input: unknown): boolean => {
  try { validWorkspaceTemplate(input); return true; } catch { return false; }
}

export interface WorkspacePolicy {
  group: Key<Group>;
  name: string;
  maxConcurrentSessionsPerUser: Natural;
  maxConcurrentSessionsPerGroup: Natural;
  maxCpuHoursPerUserPerDay: Natural;
  commandBlocklist: string | null;
  commandGreylistApproval: string | null;
}

export const validWorkspacePolicy = (input: unknown): WorkspacePolicy => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["group"] === undefined) throw new Error('`_x["group"]` required but not present');
  
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  if (typeof _x["name"] != 'string') throw new Error('`_x["name"]` must be a string');
  if (_x["maxConcurrentSessionsPerUser"] === undefined) throw new Error('`_x["maxConcurrentSessionsPerUser"]` required but not present');
  validNatural(_x["maxConcurrentSessionsPerUser"] as any);
  if (_x["maxConcurrentSessionsPerGroup"] === undefined) throw new Error('`_x["maxConcurrentSessionsPerGroup"]` required but not present');
  validNatural(_x["maxConcurrentSessionsPerGroup"] as any);
  if (_x["maxCpuHoursPerUserPerDay"] === undefined) throw new Error('`_x["maxCpuHoursPerUserPerDay"]` required but not present');
  validNatural(_x["maxCpuHoursPerUserPerDay"] as any);
  if (_x["commandBlocklist"] === undefined) throw new Error('`_x["commandBlocklist"]` required but not present');
  if (typeof _x["commandBlocklist"] != 'string' && _x["commandBlocklist"] != null) throw new Error('`_x["commandBlocklist"]` must be a string | null');
  if (_x["commandGreylistApproval"] === undefined) throw new Error('`_x["commandGreylistApproval"]` required but not present');
  if (typeof _x["commandGreylistApproval"] != 'string' && _x["commandGreylistApproval"] != null) throw new Error('`_x["commandGreylistApproval"]` must be a string | null');
  const x = input as WorkspacePolicy;
  return x;
}
export const isValidWorkspacePolicy = (input: unknown): boolean => {
  try { validWorkspacePolicy(input); return true; } catch { return false; }
}

export type EvaluationEvent = "startEval" | "evalCompleted" | "evalFailed" | "noRunner" | "cancel"

export const validEvaluationEvent = (x: unknown): EvaluationEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'startEval')) && ((v != 'evalCompleted')) && ((v != 'evalFailed')) && ((v != 'noRunner')) && ((v != 'cancel'))) throw new Error('Invalid EvaluationEvent `' + JSON.stringify(x) + '`');
  return x as EvaluationEvent;
}

export interface EvaluationPhaseContext {
  placeholder: number;
  zero: number;
}

export const validEvaluationPhaseContext = (input: unknown): EvaluationPhaseContext => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["placeholder"] === undefined) throw new Error('`_x["placeholder"]` required but not present');
  if (typeof _x["placeholder"] !== 'number' || !Number.isSafeInteger(_x["placeholder"] as number)) throw new Error('`_x["placeholder"]` must be a safe integer');
  if (_x["zero"] === undefined) throw new Error('`_x["zero"]` required but not present');
  if (typeof _x["zero"] !== 'number' || !Number.isSafeInteger(_x["zero"] as number)) throw new Error('`_x["zero"]` must be a safe integer');
  const x = input as EvaluationPhaseContext;
  return x;
}
export const isValidEvaluationPhaseContext = (input: unknown): boolean => {
  try { validEvaluationPhaseContext(input); return true; } catch { return false; }
}

export type StatusColor = "green400" | "red400" | "cyan400" | "amber400" | "zinc400" | "zinc500"

export const validStatusColor = (x: unknown): StatusColor => {
  const v = x as [string, unknown] | string;
  if(((v != 'green400')) && ((v != 'red400')) && ((v != 'cyan400')) && ((v != 'amber400')) && ((v != 'zinc400')) && ((v != 'zinc500'))) throw new Error('Invalid StatusColor `' + JSON.stringify(x) + '`');
  return x as StatusColor;
}

export interface PipelineFavicon {
  glyph: string;
  hex: string;
}

export const validPipelineFavicon = (input: unknown): PipelineFavicon => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["glyph"] === undefined) throw new Error('`_x["glyph"]` required but not present');
  if (typeof _x["glyph"] != 'string') throw new Error('`_x["glyph"]` must be a string');
  if (_x["hex"] === undefined) throw new Error('`_x["hex"]` required but not present');
  if (typeof _x["hex"] != 'string') throw new Error('`_x["hex"]` must be a string');
  const x = input as PipelineFavicon;
  return x;
}
export const isValidPipelineFavicon = (input: unknown): boolean => {
  try { validPipelineFavicon(input); return true; } catch { return false; }
}

export type StatusColorKey = "accent" | "accentMuted" | "danger" | "info" | "warning" | "muted"

export const validStatusColorKey = (x: unknown): StatusColorKey => {
  const v = x as [string, unknown] | string;
  if(((v != 'accent')) && ((v != 'accentMuted')) && ((v != 'danger')) && ((v != 'info')) && ((v != 'warning')) && ((v != 'muted'))) throw new Error('Invalid StatusColorKey `' + JSON.stringify(x) + '`');
  return x as StatusColorKey;
}

export type JobEvent = "startJobEval" | "jobEvalDone" | "startBuild" | "builderStarted" | "buildSucceeded" | "buildFailed" | "cacheHit" | "startRetry" | "cancelJob"

export const validJobEvent = (x: unknown): JobEvent => {
  const v = x as [string, unknown] | string;
  if(((v != 'startJobEval')) && ((v != 'jobEvalDone')) && ((v != 'startBuild')) && ((v != 'builderStarted')) && ((v != 'buildSucceeded')) && ((v != 'buildFailed')) && ((v != 'cacheHit')) && ((v != 'startRetry')) && ((v != 'cancelJob'))) throw new Error('Invalid JobEvent `' + JSON.stringify(x) + '`');
  return x as JobEvent;
}

export interface JobStatusContext {
  placeholder: number;
  zero: number;
}

export const validJobStatusContext = (input: unknown): JobStatusContext => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["placeholder"] === undefined) throw new Error('`_x["placeholder"]` required but not present');
  if (typeof _x["placeholder"] !== 'number' || !Number.isSafeInteger(_x["placeholder"] as number)) throw new Error('`_x["placeholder"]` must be a safe integer');
  if (_x["zero"] === undefined) throw new Error('`_x["zero"]` required but not present');
  if (typeof _x["zero"] !== 'number' || !Number.isSafeInteger(_x["zero"] as number)) throw new Error('`_x["zero"]` must be a safe integer');
  const x = input as JobStatusContext;
  return x;
}
export const isValidJobStatusContext = (input: unknown): boolean => {
  try { validJobStatusContext(input); return true; } catch { return false; }
}

export type ScopeMode = "system" | "user"

export const validScopeMode = (x: unknown): ScopeMode => {
  const v = x as [string, unknown] | string;
  if(((v != 'system')) && ((v != 'user'))) throw new Error('Invalid ScopeMode `' + JSON.stringify(x) + '`');
  return x as ScopeMode;
}

export type StreamId = "stdout" | "stderr"

export const validStreamId = (x: unknown): StreamId => {
  const v = x as [string, unknown] | string;
  if(((v != 'stdout')) && ((v != 'stderr'))) throw new Error('Invalid StreamId `' + JSON.stringify(x) + '`');
  return x as StreamId;
}

export type NixStoreMode = "daemon" | "chroot"

export const validNixStoreMode = (x: unknown): NixStoreMode => {
  const v = x as [string, unknown] | string;
  if(((v != 'daemon')) && ((v != 'chroot'))) throw new Error('Invalid NixStoreMode `' + JSON.stringify(x) + '`');
  return x as NixStoreMode;
}

export interface RunnerConfig {
  serverUrl: ServerUrl;
  serverPubkey: ServerPubkeyHex;
  keyFile: KeyFile;
  groupTokenFile: GroupTokenFile;
  groupName: GroupName;
  name: RunnerName;
  nixBin: NixBin;
  systemdRun: SystemdRunBin;
  scriptBin: ScriptBin;
  nixStoreMode: NixStoreMode;
  scopeMode: ScopeMode;
  loadIntervalMs: LoadIntervalMs;
  backoffInitialMs: BackoffInitialMs;
  backoffMaxMs: BackoffMaxMs;
}

export const validRunnerConfig = (input: unknown): RunnerConfig => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["serverUrl"] === undefined) throw new Error('`_x["serverUrl"]` required but not present');
  
  if (_x["serverPubkey"] === undefined) throw new Error('`_x["serverPubkey"]` required but not present');
  
  if (_x["keyFile"] === undefined) throw new Error('`_x["keyFile"]` required but not present');
  
  if (_x["groupTokenFile"] === undefined) throw new Error('`_x["groupTokenFile"]` required but not present');
  
  if (_x["groupName"] === undefined) throw new Error('`_x["groupName"]` required but not present');
  
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  
  if (_x["nixBin"] === undefined) throw new Error('`_x["nixBin"]` required but not present');
  
  if (_x["systemdRun"] === undefined) throw new Error('`_x["systemdRun"]` required but not present');
  
  if (_x["scriptBin"] === undefined) throw new Error('`_x["scriptBin"]` required but not present');
  
  if (_x["nixStoreMode"] === undefined) throw new Error('`_x["nixStoreMode"]` required but not present');
  validNixStoreMode(_x["nixStoreMode"] as any);
  if (_x["scopeMode"] === undefined) throw new Error('`_x["scopeMode"]` required but not present');
  validScopeMode(_x["scopeMode"] as any);
  if (_x["loadIntervalMs"] === undefined) throw new Error('`_x["loadIntervalMs"]` required but not present');
  validLoadIntervalMs(_x["loadIntervalMs"] as any);
  if (_x["backoffInitialMs"] === undefined) throw new Error('`_x["backoffInitialMs"]` required but not present');
  validBackoffInitialMs(_x["backoffInitialMs"] as any);
  if (_x["backoffMaxMs"] === undefined) throw new Error('`_x["backoffMaxMs"]` required but not present');
  validBackoffMaxMs(_x["backoffMaxMs"] as any);
  const x = input as RunnerConfig;
  return x;
}
export const isValidRunnerConfig = (input: unknown): boolean => {
  try { validRunnerConfig(input); return true; } catch { return false; }
}

export interface RunnerRegisterRequest {
  sealed: Base64Sealed;
  protocolVersion: ProtocolVersion;
}

export const validRunnerRegisterRequest = (input: unknown): RunnerRegisterRequest => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["sealed"] === undefined) throw new Error('`_x["sealed"]` required but not present');
  
  if (_x["protocolVersion"] === undefined) throw new Error('`_x["protocolVersion"]` required but not present');
  validProtocolVersion(_x["protocolVersion"] as any);
  const x = input as RunnerRegisterRequest;
  return x;
}
export const isValidRunnerRegisterRequest = (input: unknown): boolean => {
  try { validRunnerRegisterRequest(input); return true; } catch { return false; }
}

export interface Runner {
  group: Key<Group>;
  name: RunnerName;
  pubkey: Ed25519Pubkey;
  arch: NixSystem;
  cores: Cores;
  ramBytes: RamBytes;
  cpuModel: CpuModel;
  nixFeatures: NixFeatures;
  substituters: Substituters;
  nixStoreMode: NixStoreMode | null;
  lastConnectedAt: LastConnectedAt | null;
  isOnline: boolean;
  protocolVersion: ProtocolVersion | null;
}

export const validRunner = (input: unknown): Runner => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["group"] === undefined) throw new Error('`_x["group"]` required but not present');
  
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  
  if (_x["pubkey"] === undefined) throw new Error('`_x["pubkey"]` required but not present');
  
  if (_x["arch"] === undefined) throw new Error('`_x["arch"]` required but not present');
  validNixSystem(_x["arch"] as any);
  if (_x["cores"] === undefined) throw new Error('`_x["cores"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["cores"] as any)) as any);
  if (_x["ramBytes"] === undefined) throw new Error('`_x["ramBytes"]` required but not present');
  validRamBytes(_x["ramBytes"] as any);
  if (_x["cpuModel"] === undefined) throw new Error('`_x["cpuModel"]` required but not present');
  
  if (_x["nixFeatures"] === undefined) throw new Error('`_x["nixFeatures"]` required but not present');
  
  if (_x["substituters"] === undefined) throw new Error('`_x["substituters"]` required but not present');
  
  if (_x["nixStoreMode"] === undefined) throw new Error('`_x["nixStoreMode"]` required but not present');
  _x["nixStoreMode"] === null || validNixStoreMode(_x["nixStoreMode"] as any);
  if (_x["lastConnectedAt"] === undefined) throw new Error('`_x["lastConnectedAt"]` required but not present');
  _x["lastConnectedAt"] === null || validNatural(unwrapNatural(unwrapLastConnectedAt(_x["lastConnectedAt"] as any)) as any);
  // handle html checkbox bs
  if(_x["isOnline"] === undefined){
    _x["isOnline"] = false;
  }else if((_x["isOnline"] as unknown) === 'on'){
    _x["isOnline"] = true;
  }
  if (_x["isOnline"] === undefined) throw new Error('`_x["isOnline"]` required but not present');
  if (typeof _x["isOnline"] != 'boolean') throw new Error('`_x["isOnline"]` must be a boolean');
  if (_x["protocolVersion"] === undefined) throw new Error('`_x["protocolVersion"]` required but not present');
  _x["protocolVersion"] === null || validProtocolVersion(_x["protocolVersion"] as any);
  const x = input as Runner;
  return x;
}
export const isValidRunner = (input: unknown): boolean => {
  try { validRunner(input); return true; } catch { return false; }
}

export type DispatchMode = "heuristic" | "bandit"

export const validDispatchMode = (x: unknown): DispatchMode => {
  const v = x as [string, unknown] | string;
  if(((v != 'heuristic')) && ((v != 'bandit'))) throw new Error('Invalid DispatchMode `' + JSON.stringify(x) + '`');
  return x as DispatchMode;
}

export interface BanditDispatch {
  job: Key<CiJob>;
  dispatchedAt: DispatchedAt;
  mode: DispatchMode;
  sharedFeaturesJson: SharedFeaturesJson;
  actionFeaturesJson: ActionFeaturesJson;
  chosenIdx: ChosenIdx;
  banditProbability: BanditProbability | null;
  cost: BanditCost | null;
  outcomeAt: OutcomeAt | null;
  replayedAt: ReplayedAt | null;
}

export const validBanditDispatch = (input: unknown): BanditDispatch => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["job"] === undefined) throw new Error('`_x["job"]` required but not present');
  
  if (_x["dispatchedAt"] === undefined) throw new Error('`_x["dispatchedAt"]` required but not present');
  validBigNatural(unwrapBigNatural(unwrapDispatchedAt(_x["dispatchedAt"] as any)) as any);
  if (_x["mode"] === undefined) throw new Error('`_x["mode"]` required but not present');
  validDispatchMode(_x["mode"] as any);
  if (_x["sharedFeaturesJson"] === undefined) throw new Error('`_x["sharedFeaturesJson"]` required but not present');
  
  if (_x["actionFeaturesJson"] === undefined) throw new Error('`_x["actionFeaturesJson"]` required but not present');
  
  if (_x["chosenIdx"] === undefined) throw new Error('`_x["chosenIdx"]` required but not present');
  validNatural(unwrapNatural(unwrapChosenIdx(_x["chosenIdx"] as any)) as any);
  if (_x["banditProbability"] === undefined) throw new Error('`_x["banditProbability"]` required but not present');
  _x["banditProbability"] === null || validProbability(unwrapProbability(unwrapBanditProbability(_x["banditProbability"] as any)) as any);
  if (_x["cost"] === undefined) throw new Error('`_x["cost"]` required but not present');
  
  if (_x["outcomeAt"] === undefined) throw new Error('`_x["outcomeAt"]` required but not present');
  _x["outcomeAt"] === null || validBigNatural(unwrapBigNatural(unwrapOutcomeAt(_x["outcomeAt"] as any)) as any);
  if (_x["replayedAt"] === undefined) throw new Error('`_x["replayedAt"]` required but not present');
  _x["replayedAt"] === null || validBigNatural(unwrapBigNatural(unwrapReplayedAt(_x["replayedAt"] as any)) as any);
  const x = input as BanditDispatch;
  return x;
}
export const isValidBanditDispatch = (input: unknown): boolean => {
  try { validBanditDispatch(input); return true; } catch { return false; }
}

export interface BanditSnapshot {
  eventsThrough: OutcomeAt;
  eventCount: Natural;
  modelBase64: string;
  vwVersion: string;
}

export const validBanditSnapshot = (input: unknown): BanditSnapshot => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["eventsThrough"] === undefined) throw new Error('`_x["eventsThrough"]` required but not present');
  validBigNatural(unwrapBigNatural(unwrapOutcomeAt(_x["eventsThrough"] as any)) as any);
  if (_x["eventCount"] === undefined) throw new Error('`_x["eventCount"]` required but not present');
  validNatural(_x["eventCount"] as any);
  if (_x["modelBase64"] === undefined) throw new Error('`_x["modelBase64"]` required but not present');
  if (typeof _x["modelBase64"] != 'string') throw new Error('`_x["modelBase64"]` must be a string');
  if (_x["vwVersion"] === undefined) throw new Error('`_x["vwVersion"]` required but not present');
  if (typeof _x["vwVersion"] != 'string') throw new Error('`_x["vwVersion"]` must be a string');
  const x = input as BanditSnapshot;
  return x;
}
export const isValidBanditSnapshot = (input: unknown): boolean => {
  try { validBanditSnapshot(input); return true; } catch { return false; }
}

export interface JobHistory {
  job: Key<CiJob>;
  runner: Key<Runner> | null;
  startedAt: StartedAt;
  endedAt: EndedAt;
  durationMs: DurationMs;
  exitCode: ExitCode;
  cacheHit: boolean;
  peakRssBytes: RamBytes | null;
  closureSize: ClosureSize | null;
  drvHash: DrvHash | null;
  arch: NixSystem;
  succeeded: boolean;
  dispatchMode: DispatchMode;
  banditProbability: BanditProbability | null;
}

export const validJobHistory = (input: unknown): JobHistory => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["job"] === undefined) throw new Error('`_x["job"]` required but not present');
  
  if (_x["runner"] === undefined) throw new Error('`_x["runner"]` required but not present');
  
  if (_x["startedAt"] === undefined) throw new Error('`_x["startedAt"]` required but not present');
  validBigNatural(unwrapBigNatural(unwrapStartedAt(_x["startedAt"] as any)) as any);
  if (_x["endedAt"] === undefined) throw new Error('`_x["endedAt"]` required but not present');
  validBigNatural(unwrapBigNatural(unwrapEndedAt(_x["endedAt"] as any)) as any);
  if (_x["durationMs"] === undefined) throw new Error('`_x["durationMs"]` required but not present');
  validNatural(unwrapNatural(unwrapDurationMs(_x["durationMs"] as any)) as any);
  if (_x["exitCode"] === undefined) throw new Error('`_x["exitCode"]` required but not present');
  validExitCode(_x["exitCode"] as any);
  // handle html checkbox bs
  if(_x["cacheHit"] === undefined){
    _x["cacheHit"] = false;
  }else if((_x["cacheHit"] as unknown) === 'on'){
    _x["cacheHit"] = true;
  }
  if (_x["cacheHit"] === undefined) throw new Error('`_x["cacheHit"]` required but not present');
  if (typeof _x["cacheHit"] != 'boolean') throw new Error('`_x["cacheHit"]` must be a boolean');
  if (_x["peakRssBytes"] === undefined) throw new Error('`_x["peakRssBytes"]` required but not present');
  _x["peakRssBytes"] === null || validRamBytes(_x["peakRssBytes"] as any);
  if (_x["closureSize"] === undefined) throw new Error('`_x["closureSize"]` required but not present');
  _x["closureSize"] === null || validNatural(unwrapNatural(unwrapClosureSize(_x["closureSize"] as any)) as any);
  if (_x["drvHash"] === undefined) throw new Error('`_x["drvHash"]` required but not present');
  
  if (_x["arch"] === undefined) throw new Error('`_x["arch"]` required but not present');
  validNixSystem(_x["arch"] as any);
  // handle html checkbox bs
  if(_x["succeeded"] === undefined){
    _x["succeeded"] = false;
  }else if((_x["succeeded"] as unknown) === 'on'){
    _x["succeeded"] = true;
  }
  if (_x["succeeded"] === undefined) throw new Error('`_x["succeeded"]` required but not present');
  if (typeof _x["succeeded"] != 'boolean') throw new Error('`_x["succeeded"]` must be a boolean');
  if (_x["dispatchMode"] === undefined) throw new Error('`_x["dispatchMode"]` required but not present');
  validDispatchMode(_x["dispatchMode"] as any);
  if (_x["banditProbability"] === undefined) throw new Error('`_x["banditProbability"]` required but not present');
  _x["banditProbability"] === null || validProbability(unwrapProbability(unwrapBanditProbability(_x["banditProbability"] as any)) as any);
  const x = input as JobHistory;
  return x;
}
export const isValidJobHistory = (input: unknown): boolean => {
  try { validJobHistory(input); return true; } catch { return false; }
}

export interface Capabilities {
  cores: Cores;
  ramBytes: RamBytes;
  arch: NixSystem;
  cpuModel: CpuModel;
  nixFeatures: NixFeatures;
  substituters: Substituters;
  nixStoreMode: NixStoreMode;
}

export const validCapabilities = (input: unknown): Capabilities => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["cores"] === undefined) throw new Error('`_x["cores"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["cores"] as any)) as any);
  if (_x["ramBytes"] === undefined) throw new Error('`_x["ramBytes"]` required but not present');
  validRamBytes(_x["ramBytes"] as any);
  if (_x["arch"] === undefined) throw new Error('`_x["arch"]` required but not present');
  validNixSystem(_x["arch"] as any);
  if (_x["cpuModel"] === undefined) throw new Error('`_x["cpuModel"]` required but not present');
  
  if (_x["nixFeatures"] === undefined) throw new Error('`_x["nixFeatures"]` required but not present');
  
  if (_x["substituters"] === undefined) throw new Error('`_x["substituters"]` required but not present');
  
  if (_x["nixStoreMode"] === undefined) throw new Error('`_x["nixStoreMode"]` required but not present');
  validNixStoreMode(_x["nixStoreMode"] as any);
  const x = input as Capabilities;
  return x;
}
export const isValidCapabilities = (input: unknown): boolean => {
  try { validCapabilities(input); return true; } catch { return false; }
}

export interface LoadSample {
  load1: LoadAverage;
  load5: LoadAverage;
  freeRamBytes: RamBytes;
  activeJobs: Cores;
}

export const validLoadSample = (input: unknown): LoadSample => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["load1"] === undefined) throw new Error('`_x["load1"]` required but not present');
  validLoadAverage(_x["load1"] as any);
  if (_x["load5"] === undefined) throw new Error('`_x["load5"]` required but not present');
  validLoadAverage(_x["load5"] as any);
  if (_x["freeRamBytes"] === undefined) throw new Error('`_x["freeRamBytes"]` required but not present');
  validRamBytes(_x["freeRamBytes"] as any);
  if (_x["activeJobs"] === undefined) throw new Error('`_x["activeJobs"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["activeJobs"] as any)) as any);
  const x = input as LoadSample;
  return x;
}
export const isValidLoadSample = (input: unknown): boolean => {
  try { validLoadSample(input); return true; } catch { return false; }
}

export interface JobSample {
  jobId: JobIdWire;
  cpuUsageUs: BigNatural;
  memoryBytes: RamBytes;
  tsMs: BigNatural;
}

export const validJobSample = (input: unknown): JobSample => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["jobId"] === undefined) throw new Error('`_x["jobId"]` required but not present');
  
  if (_x["cpuUsageUs"] === undefined) throw new Error('`_x["cpuUsageUs"]` required but not present');
  validBigNatural(_x["cpuUsageUs"] as any);
  if (_x["memoryBytes"] === undefined) throw new Error('`_x["memoryBytes"]` required but not present');
  validRamBytes(_x["memoryBytes"] as any);
  if (_x["tsMs"] === undefined) throw new Error('`_x["tsMs"]` required but not present');
  validBigNatural(_x["tsMs"] as any);
  const x = input as JobSample;
  return x;
}
export const isValidJobSample = (input: unknown): boolean => {
  try { validJobSample(input); return true; } catch { return false; }
}

export interface HostSample {
  cpuUsageUs: BigNatural;
  memoryUsedBytes: RamBytes;
  memoryTotalBytes: RamBytes;
  tsMs: BigNatural;
}

export const validHostSample = (input: unknown): HostSample => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["cpuUsageUs"] === undefined) throw new Error('`_x["cpuUsageUs"]` required but not present');
  validBigNatural(_x["cpuUsageUs"] as any);
  if (_x["memoryUsedBytes"] === undefined) throw new Error('`_x["memoryUsedBytes"]` required but not present');
  validRamBytes(_x["memoryUsedBytes"] as any);
  if (_x["memoryTotalBytes"] === undefined) throw new Error('`_x["memoryTotalBytes"]` required but not present');
  validRamBytes(_x["memoryTotalBytes"] as any);
  if (_x["tsMs"] === undefined) throw new Error('`_x["tsMs"]` required but not present');
  validBigNatural(_x["tsMs"] as any);
  const x = input as HostSample;
  return x;
}
export const isValidHostSample = (input: unknown): boolean => {
  try { validHostSample(input); return true; } catch { return false; }
}

export interface RunnerMetricsPayload {
  runnerId: Natural;
  cpuUs: BigNatural;
  memBytes: RamBytes;
  tsMs: BigNatural;
}

export const validRunnerMetricsPayload = (input: unknown): RunnerMetricsPayload => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["runnerId"] === undefined) throw new Error('`_x["runnerId"]` required but not present');
  validNatural(_x["runnerId"] as any);
  if (_x["cpuUs"] === undefined) throw new Error('`_x["cpuUs"]` required but not present');
  validBigNatural(_x["cpuUs"] as any);
  if (_x["memBytes"] === undefined) throw new Error('`_x["memBytes"]` required but not present');
  validRamBytes(_x["memBytes"] as any);
  if (_x["tsMs"] === undefined) throw new Error('`_x["tsMs"]` required but not present');
  validBigNatural(_x["tsMs"] as any);
  const x = input as RunnerMetricsPayload;
  return x;
}
export const isValidRunnerMetricsPayload = (input: unknown): boolean => {
  try { validRunnerMetricsPayload(input); return true; } catch { return false; }
}

export type EvalKind = "pipelineEval" | "dispatchEval" | "appEval" | "moduleEval" | "templateEval" | "refBuild" | "flakeCheck"

export const validEvalKind = (x: unknown): EvalKind => {
  const v = x as [string, unknown] | string;
  if(((v != 'pipelineEval')) && ((v != 'dispatchEval')) && ((v != 'appEval')) && ((v != 'moduleEval')) && ((v != 'templateEval')) && ((v != 'refBuild')) && ((v != 'flakeCheck'))) throw new Error('Invalid EvalKind `' + JSON.stringify(x) + '`');
  return x as EvalKind;
}

export interface EvalSpec {
  flakeUrl: string;
  kind: EvalKind;
  attrPaths: AttrPath[];
  system: NixSystem;
  secrets: SealedSecret[];
  idleMs: BigNatural;
  evalId: EvalIdWire;
}

export const validEvalSpec = (input: unknown): EvalSpec => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["flakeUrl"] === undefined) throw new Error('`_x["flakeUrl"]` required but not present');
  if (typeof _x["flakeUrl"] != 'string') throw new Error('`_x["flakeUrl"]` must be a string');
  if (_x["kind"] === undefined) throw new Error('`_x["kind"]` required but not present');
  validEvalKind(_x["kind"] as any);
  if (_x["attrPaths"] === undefined) throw new Error('`_x["attrPaths"]` required but not present');
    if(_x["attrPaths"] instanceof Array){
    if((_x["attrPaths"] as any)?.length){
      (_x["attrPaths"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  
      });
    }else{
      _x["attrPaths"] = _x["attrPaths"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["attrPaths"]` must be an Array');
  }
  if (_x["system"] === undefined) throw new Error('`_x["system"]` required but not present');
  validNixSystem(_x["system"] as any);
  if (_x["secrets"] === undefined) throw new Error('`_x["secrets"]` required but not present');
    if(_x["secrets"] instanceof Array){
    if((_x["secrets"] as any)?.length){
      (_x["secrets"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validSealedSecret(y as any);
      });
    }else{
      _x["secrets"] = _x["secrets"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["secrets"]` must be an Array');
  }
  if (_x["idleMs"] === undefined) throw new Error('`_x["idleMs"]` required but not present');
  validBigNatural(_x["idleMs"] as any);
  if (_x["evalId"] === undefined) throw new Error('`_x["evalId"]` required but not present');
  
  const x = input as EvalSpec;
  return x;
}
export const isValidEvalSpec = (input: unknown): boolean => {
  try { validEvalSpec(input); return true; } catch { return false; }
}

export interface EvalLeaf {
  attrPath: AttrPath;
  drvPath: DerivationPath;
  system: NixSystem | null;
  outputType: FlakeOutputType | null;
  name: string;
  inputDrvs: DerivationPath[];
  outputPaths: OutputPathEntry[];
}

export const validEvalLeaf = (input: unknown): EvalLeaf => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["attrPath"] === undefined) throw new Error('`_x["attrPath"]` required but not present');
  
  if (_x["drvPath"] === undefined) throw new Error('`_x["drvPath"]` required but not present');
  
  if (_x["system"] === undefined) throw new Error('`_x["system"]` required but not present');
  _x["system"] === null || validNixSystem(_x["system"] as any);
  if (_x["outputType"] === undefined) throw new Error('`_x["outputType"]` required but not present');
  _x["outputType"] === null || validFlakeOutputType(_x["outputType"] as any);
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  if (typeof _x["name"] != 'string') throw new Error('`_x["name"]` must be a string');
  if (_x["inputDrvs"] === undefined) throw new Error('`_x["inputDrvs"]` required but not present');
    if(_x["inputDrvs"] instanceof Array){
    if((_x["inputDrvs"] as any)?.length){
      (_x["inputDrvs"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  
      });
    }else{
      _x["inputDrvs"] = _x["inputDrvs"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["inputDrvs"]` must be an Array');
  }
  if (_x["outputPaths"] === undefined) throw new Error('`_x["outputPaths"]` required but not present');
    if(_x["outputPaths"] instanceof Array){
    if((_x["outputPaths"] as any)?.length){
      (_x["outputPaths"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validOutputPathEntry(y as any);
      });
    }else{
      _x["outputPaths"] = _x["outputPaths"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["outputPaths"]` must be an Array');
  }
  const x = input as EvalLeaf;
  return x;
}
export const isValidEvalLeaf = (input: unknown): boolean => {
  try { validEvalLeaf(input); return true; } catch { return false; }
}

export interface OutputPathEntry {
  name: string;
  storePath: StorePath;
}

export const validOutputPathEntry = (input: unknown): OutputPathEntry => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  if (typeof _x["name"] != 'string') throw new Error('`_x["name"]` must be a string');
  if (_x["storePath"] === undefined) throw new Error('`_x["storePath"]` required but not present');
  
  const x = input as OutputPathEntry;
  return x;
}
export const isValidOutputPathEntry = (input: unknown): boolean => {
  try { validOutputPathEntry(input); return true; } catch { return false; }
}

export interface EvalFailure {
  exitCode: ExitCode;
  stderr: string;
}

export const validEvalFailure = (input: unknown): EvalFailure => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["exitCode"] === undefined) throw new Error('`_x["exitCode"]` required but not present');
  validExitCode(_x["exitCode"] as any);
  if (_x["stderr"] === undefined) throw new Error('`_x["stderr"]` required but not present');
  if (typeof _x["stderr"] != 'string') throw new Error('`_x["stderr"]` must be a string');
  const x = input as EvalFailure;
  return x;
}
export const isValidEvalFailure = (input: unknown): boolean => {
  try { validEvalFailure(input); return true; } catch { return false; }
}

export type EvalOutcome = [ "success", EvalSuccess ] | [ "failure", EvalFailure ]

export const validEvalOutcome = (x: unknown): EvalOutcome => {
  const v = x as [string, unknown] | string;
  if((!(v[0] === 'success' && validEvalSuccess(v[1] as EvalSuccess)) && (v[0] != undefined && v[0] != 'success')) && (!(v[0] === 'failure' && validEvalFailure(v[1] as EvalFailure)) && (v[0] != undefined && v[0] != 'failure'))) throw new Error('Invalid EvalOutcome `' + JSON.stringify(x) + '`');
  return x as EvalOutcome;
}

export interface EvalResult {
  evalId: EvalIdWire;
  outcome: EvalOutcome;
  durationMs: BigNatural;
  evaluatedAtMs: TsUnixMs;
}

export const validEvalResult = (input: unknown): EvalResult => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["evalId"] === undefined) throw new Error('`_x["evalId"]` required but not present');
  
  if (_x["outcome"] === undefined) throw new Error('`_x["outcome"]` required but not present');
  validEvalOutcome(_x["outcome"] as any);
  if (_x["durationMs"] === undefined) throw new Error('`_x["durationMs"]` required but not present');
  validBigNatural(_x["durationMs"] as any);
  if (_x["evaluatedAtMs"] === undefined) throw new Error('`_x["evaluatedAtMs"]` required but not present');
  
  const x = input as EvalResult;
  return x;
}
export const isValidEvalResult = (input: unknown): boolean => {
  try { validEvalResult(input); return true; } catch { return false; }
}

export interface EvalStart {
  evalId: EvalIdWire;
  spec: EvalSpec;
}

export const validEvalStart = (input: unknown): EvalStart => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["evalId"] === undefined) throw new Error('`_x["evalId"]` required but not present');
  
  if (_x["spec"] === undefined) throw new Error('`_x["spec"]` required but not present');
  validEvalSpec(_x["spec"] as any);
  const x = input as EvalStart;
  return x;
}
export const isValidEvalStart = (input: unknown): boolean => {
  try { validEvalStart(input); return true; } catch { return false; }
}

export interface JobSpec {
  job: Key<CiJob>;
  drvHash: DrvHash | null;
  arch: NixSystem;
  expectedCores: Cores;
  expectedRam: RamBytes;
  secrets: SealedSecret[];
  drvPath: string;
}

export const validJobSpec = (input: unknown): JobSpec => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["job"] === undefined) throw new Error('`_x["job"]` required but not present');
  
  if (_x["drvHash"] === undefined) throw new Error('`_x["drvHash"]` required but not present');
  
  if (_x["arch"] === undefined) throw new Error('`_x["arch"]` required but not present');
  validNixSystem(_x["arch"] as any);
  if (_x["expectedCores"] === undefined) throw new Error('`_x["expectedCores"]` required but not present');
  validNatural(unwrapNatural(unwrapCores(_x["expectedCores"] as any)) as any);
  if (_x["expectedRam"] === undefined) throw new Error('`_x["expectedRam"]` required but not present');
  validRamBytes(_x["expectedRam"] as any);
  if (_x["secrets"] === undefined) throw new Error('`_x["secrets"]` required but not present');
    if(_x["secrets"] instanceof Array){
    if((_x["secrets"] as any)?.length){
      (_x["secrets"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validSealedSecret(y as any);
      });
    }else{
      _x["secrets"] = _x["secrets"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["secrets"]` must be an Array');
  }
  if (_x["drvPath"] === undefined) throw new Error('`_x["drvPath"]` required but not present');
  if (typeof _x["drvPath"] != 'string') throw new Error('`_x["drvPath"]` must be a string');
  const x = input as JobSpec;
  return x;
}
export const isValidJobSpec = (input: unknown): boolean => {
  try { validJobSpec(input); return true; } catch { return false; }
}

export interface Hello {
  groupName: GroupName;
  runnerName: RunnerName;
  pubkey: Ed25519Pubkey;
}

export const validHello = (input: unknown): Hello => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["groupName"] === undefined) throw new Error('`_x["groupName"]` required but not present');
  
  if (_x["runnerName"] === undefined) throw new Error('`_x["runnerName"]` required but not present');
  
  if (_x["pubkey"] === undefined) throw new Error('`_x["pubkey"]` required but not present');
  
  const x = input as Hello;
  return x;
}
export const isValidHello = (input: unknown): boolean => {
  try { validHello(input); return true; } catch { return false; }
}

export interface JobStart {
  jobId: JobIdWire;
  spec: JobSpec;
}

export const validJobStart = (input: unknown): JobStart => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["jobId"] === undefined) throw new Error('`_x["jobId"]` required but not present');
  
  if (_x["spec"] === undefined) throw new Error('`_x["spec"]` required but not present');
  validJobSpec(_x["spec"] as any);
  const x = input as JobStart;
  return x;
}
export const isValidJobStart = (input: unknown): boolean => {
  try { validJobStart(input); return true; } catch { return false; }
}

export interface JobRejected {
  jobId: JobIdWire;
  reason: RejectionReason;
}

export const validJobRejected = (input: unknown): JobRejected => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["jobId"] === undefined) throw new Error('`_x["jobId"]` required but not present');
  
  if (_x["reason"] === undefined) throw new Error('`_x["reason"]` required but not present');
  
  const x = input as JobRejected;
  return x;
}
export const isValidJobRejected = (input: unknown): boolean => {
  try { validJobRejected(input); return true; } catch { return false; }
}

export interface JobExit {
  jobId: JobIdWire;
  exitCode: ExitCode;
  peakRssBytes: RamBytes | null;
}

export const validJobExit = (input: unknown): JobExit => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["jobId"] === undefined) throw new Error('`_x["jobId"]` required but not present');
  
  if (_x["exitCode"] === undefined) throw new Error('`_x["exitCode"]` required but not present');
  validExitCode(_x["exitCode"] as any);
  if (_x["peakRssBytes"] === undefined) throw new Error('`_x["peakRssBytes"]` required but not present');
  _x["peakRssBytes"] === null || validRamBytes(_x["peakRssBytes"] as any);
  const x = input as JobExit;
  return x;
}
export const isValidJobExit = (input: unknown): boolean => {
  try { validJobExit(input); return true; } catch { return false; }
}

export type ServerToRunnerMsg = [ "challenge", Base64Nonce ] | [ "jobStart", JobStart ] | [ "jobCancel", JobIdWire ] | [ "evalStart", EvalStart ] | [ "evalCancel", EvalIdWire ] | "ping"

export const validServerToRunnerMsg = (x: unknown): ServerToRunnerMsg => {
  const v = x as [string, unknown] | string;
  if((!(v[0] === 'challenge' && validBase64Nonce(v[1] as Base64Nonce)) && (v[0] != undefined && v[0] != 'challenge')) && (!(v[0] === 'jobStart' && validJobStart(v[1] as JobStart)) && (v[0] != undefined && v[0] != 'jobStart')) && (!(v[0] === 'jobCancel' && validJobIdWire(v[1] as JobIdWire)) && (v[0] != undefined && v[0] != 'jobCancel')) && (!(v[0] === 'evalStart' && validEvalStart(v[1] as EvalStart)) && (v[0] != undefined && v[0] != 'evalStart')) && (!(v[0] === 'evalCancel' && validEvalIdWire(v[1] as EvalIdWire)) && (v[0] != undefined && v[0] != 'evalCancel')) && ((v != 'ping'))) throw new Error('Invalid ServerToRunnerMsg `' + JSON.stringify(x) + '`');
  return x as ServerToRunnerMsg;
}

export type RunnerToServerMsg = [ "hello", Hello ] | [ "auth", Base64Signature ] | [ "capabilities", Capabilities ] | [ "load", LoadSample ] | [ "jobSample", JobSample ] | [ "hostSample", HostSample ] | [ "jobAccepted", JobIdWire ] | [ "jobRejected", JobRejected ] | [ "builderStarted", JobIdWire ] | [ "jobExit", JobExit ] | [ "evalAccepted", EvalIdWire ] | [ "evalResult", EvalResult ] | "pong"

export const validRunnerToServerMsg = (x: unknown): RunnerToServerMsg => {
  const v = x as [string, unknown] | string;
  if((!(v[0] === 'hello' && validHello(v[1] as Hello)) && (v[0] != undefined && v[0] != 'hello')) && (!(v[0] === 'auth' && validBase64Signature(v[1] as Base64Signature)) && (v[0] != undefined && v[0] != 'auth')) && (!(v[0] === 'capabilities' && validCapabilities(v[1] as Capabilities)) && (v[0] != undefined && v[0] != 'capabilities')) && (!(v[0] === 'load' && validLoadSample(v[1] as LoadSample)) && (v[0] != undefined && v[0] != 'load')) && (!(v[0] === 'jobSample' && validJobSample(v[1] as JobSample)) && (v[0] != undefined && v[0] != 'jobSample')) && (!(v[0] === 'hostSample' && validHostSample(v[1] as HostSample)) && (v[0] != undefined && v[0] != 'hostSample')) && (!(v[0] === 'jobAccepted' && validJobIdWire(v[1] as JobIdWire)) && (v[0] != undefined && v[0] != 'jobAccepted')) && (!(v[0] === 'jobRejected' && validJobRejected(v[1] as JobRejected)) && (v[0] != undefined && v[0] != 'jobRejected')) && (!(v[0] === 'builderStarted' && validJobIdWire(v[1] as JobIdWire)) && (v[0] != undefined && v[0] != 'builderStarted')) && (!(v[0] === 'jobExit' && validJobExit(v[1] as JobExit)) && (v[0] != undefined && v[0] != 'jobExit')) && (!(v[0] === 'evalAccepted' && validEvalIdWire(v[1] as EvalIdWire)) && (v[0] != undefined && v[0] != 'evalAccepted')) && (!(v[0] === 'evalResult' && validEvalResult(v[1] as EvalResult)) && (v[0] != undefined && v[0] != 'evalResult')) && ((v != 'pong'))) throw new Error('Invalid RunnerToServerMsg `' + JSON.stringify(x) + '`');
  return x as RunnerToServerMsg;
}

export interface RegisterPayload {
  protocolVersion: ProtocolVersion;
  groupId: GroupTokenSecret;
  groupName: GroupName;
  runnerName: RunnerName;
  runnerPubkey: Ed25519Pubkey;
  capabilities: Capabilities;
  tsMs: TsUnixMs;
  nonce: Base64Nonce;
  selfSig: Base64Signature;
}

export const validRegisterPayload = (input: unknown): RegisterPayload => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["protocolVersion"] === undefined) throw new Error('`_x["protocolVersion"]` required but not present');
  validProtocolVersion(_x["protocolVersion"] as any);
  if (_x["groupId"] === undefined) throw new Error('`_x["groupId"]` required but not present');
  
  if (_x["groupName"] === undefined) throw new Error('`_x["groupName"]` required but not present');
  
  if (_x["runnerName"] === undefined) throw new Error('`_x["runnerName"]` required but not present');
  
  if (_x["runnerPubkey"] === undefined) throw new Error('`_x["runnerPubkey"]` required but not present');
  
  if (_x["capabilities"] === undefined) throw new Error('`_x["capabilities"]` required but not present');
  validCapabilities(_x["capabilities"] as any);
  if (_x["tsMs"] === undefined) throw new Error('`_x["tsMs"]` required but not present');
  
  if (_x["nonce"] === undefined) throw new Error('`_x["nonce"]` required but not present');
  
  if (_x["selfSig"] === undefined) throw new Error('`_x["selfSig"]` required but not present');
  
  const x = input as RegisterPayload;
  return x;
}
export const isValidRegisterPayload = (input: unknown): boolean => {
  try { validRegisterPayload(input); return true; } catch { return false; }
}

export type PipelineStatus = "pending" | "evaluationPending" | "evaluating" | "queued" | "building" | "succeeded" | "failed" | "cancelled"

export const validPipelineStatus = (x: unknown): PipelineStatus => {
  const v = x as [string, unknown] | string;
  if(((v != 'pending')) && ((v != 'evaluationPending')) && ((v != 'evaluating')) && ((v != 'queued')) && ((v != 'building')) && ((v != 'succeeded')) && ((v != 'failed')) && ((v != 'cancelled'))) throw new Error('Invalid PipelineStatus `' + JSON.stringify(x) + '`');
  return x as PipelineStatus;
}

export type JobStatus = "queued" | "evaluating" | "preparing" | "building" | "retrying" | "cached" | "succeeded" | "failed" | "cancelled"

export const validJobStatus = (x: unknown): JobStatus => {
  const v = x as [string, unknown] | string;
  if(((v != 'queued')) && ((v != 'evaluating')) && ((v != 'preparing')) && ((v != 'building')) && ((v != 'retrying')) && ((v != 'cached')) && ((v != 'succeeded')) && ((v != 'failed')) && ((v != 'cancelled'))) throw new Error('Invalid JobStatus `' + JSON.stringify(x) + '`');
  return x as JobStatus;
}

export type EvaluationPhase = "pending" | "evaluationPending" | "evaluating" | "evaluated" | "evalFailed" | "cancelled"

export const validEvaluationPhase = (x: unknown): EvaluationPhase => {
  const v = x as [string, unknown] | string;
  if(((v != 'pending')) && ((v != 'evaluationPending')) && ((v != 'evaluating')) && ((v != 'evaluated')) && ((v != 'evalFailed')) && ((v != 'cancelled'))) throw new Error('Invalid EvaluationPhase `' + JSON.stringify(x) + '`');
  return x as EvaluationPhase;
}

export type PipelineFailureReason = "noRunnerForSystem" | "noRunnerCapableOfSystem" | "noRunnerAtEvalTime" | "nixEvalFailed" | "evalRunnerDisconnected"

export const validPipelineFailureReason = (x: unknown): PipelineFailureReason => {
  const v = x as [string, unknown] | string;
  if(((v != 'noRunnerForSystem')) && ((v != 'noRunnerCapableOfSystem')) && ((v != 'noRunnerAtEvalTime')) && ((v != 'nixEvalFailed')) && ((v != 'evalRunnerDisconnected'))) throw new Error('Invalid PipelineFailureReason `' + JSON.stringify(x) + '`');
  return x as PipelineFailureReason;
}

export type FailureClass = "buildFailure" | "transientFailure" | "evaluationFailure" | "timeout" | "infrastructureError" | "unknownFailure"

export const validFailureClass = (x: unknown): FailureClass => {
  const v = x as [string, unknown] | string;
  if(((v != 'buildFailure')) && ((v != 'transientFailure')) && ((v != 'evaluationFailure')) && ((v != 'timeout')) && ((v != 'infrastructureError')) && ((v != 'unknownFailure'))) throw new Error('Invalid FailureClass `' + JSON.stringify(x) + '`');
  return x as FailureClass;
}

export type RetryBackoff = "exponential" | "linear" | "fixed"

export const validRetryBackoff = (x: unknown): RetryBackoff => {
  const v = x as [string, unknown] | string;
  if(((v != 'exponential')) && ((v != 'linear')) && ((v != 'fixed'))) throw new Error('Invalid RetryBackoff `' + JSON.stringify(x) + '`');
  return x as RetryBackoff;
}

export type FlakeOutputType = "check" | "package" | "devShell" | "nixosConfiguration" | "darwinConfiguration" | "hydraJob" | "custom" | "app" | "flakeCheck" | "dispatch" | "formatter" | "template" | "module"

export const validFlakeOutputType = (x: unknown): FlakeOutputType => {
  const v = x as [string, unknown] | string;
  if(((v != 'check')) && ((v != 'package')) && ((v != 'devShell')) && ((v != 'nixosConfiguration')) && ((v != 'darwinConfiguration')) && ((v != 'hydraJob')) && ((v != 'custom')) && ((v != 'app')) && ((v != 'flakeCheck')) && ((v != 'dispatch')) && ((v != 'formatter')) && ((v != 'template')) && ((v != 'module'))) throw new Error('Invalid FlakeOutputType `' + JSON.stringify(x) + '`');
  return x as FlakeOutputType;
}

export type FlakeOutputInternalType = "check" | "package" | "devShell" | "nixosConfiguration" | "darwinConfiguration" | "hydraJob" | "custom" | "app" | "formatter" | "template" | "nixosModule"

export const validFlakeOutputInternalType = (x: unknown): FlakeOutputInternalType => {
  const v = x as [string, unknown] | string;
  if(((v != 'check')) && ((v != 'package')) && ((v != 'devShell')) && ((v != 'nixosConfiguration')) && ((v != 'darwinConfiguration')) && ((v != 'hydraJob')) && ((v != 'custom')) && ((v != 'app')) && ((v != 'formatter')) && ((v != 'template')) && ((v != 'nixosModule'))) throw new Error('Invalid FlakeOutputInternalType `' + JSON.stringify(x) + '`');
  return x as FlakeOutputInternalType;
}

export type NixSystem = "amdLinux" | "armLinux" | "armDarwin" | "amdDarwin"

export const validNixSystem = (x: unknown): NixSystem => {
  const v = x as [string, unknown] | string;
  if(((v != 'amdLinux')) && ((v != 'armLinux')) && ((v != 'armDarwin')) && ((v != 'amdDarwin'))) throw new Error('Invalid NixSystem `' + JSON.stringify(x) + '`');
  return x as NixSystem;
}

export type AttemptStatus = "running" | "succeeded" | "failed" | "timedOut" | "cancelled"

export const validAttemptStatus = (x: unknown): AttemptStatus => {
  const v = x as [string, unknown] | string;
  if(((v != 'running')) && ((v != 'succeeded')) && ((v != 'failed')) && ((v != 'timedOut')) && ((v != 'cancelled'))) throw new Error('Invalid AttemptStatus `' + JSON.stringify(x) + '`');
  return x as AttemptStatus;
}

export type EvalStatus = "pending" | "running" | "succeeded" | "failed"

export const validEvalStatus = (x: unknown): EvalStatus => {
  const v = x as [string, unknown] | string;
  if(((v != 'pending')) && ((v != 'running')) && ((v != 'succeeded')) && ((v != 'failed'))) throw new Error('Invalid EvalStatus `' + JSON.stringify(x) + '`');
  return x as EvalStatus;
}

export interface Pipeline {
  repo: Key<Repo>;
  commitSha: CommitSha;
  branch: BranchName;
  evaluationPhase: EvaluationPhase;
  status: PipelineStatus;
  failureReason: PipelineFailureReason | null;
  flakeLockHash: FlakeLockHash | null;
  retryMaxAttempts: RetryMaxAttempts;
  retryBaseDelayMs: RetryBaseDelayMs;
  retryBackoff: RetryBackoff;
  totalJobs: JobCount;
  cachedJobs: JobCount;
  builtJobs: JobCount;
  failedJobs: JobCount;
  retryCount: RetryCount;
  flakyJobCount: JobCount;
  wallClockDurationMs: DurationMs | null;
  sumJobDurationMs: DurationMs | null;
}

export interface CiJob {
  pipeline: Key<Pipeline>;
  derivationPath: DerivationPath | null;
  drvHash: DrvHash | null;
  outputAttrPath: AttrPath | null;
  nixSystem: NixSystem | null;
  outputType: FlakeOutputType | null;
  status: JobStatus;
  isFlaky: boolean;
  storePath: StorePath | null;
  cacheHit: boolean;
  closureSize: ClosureSize | null;
  isUserOutput: boolean;
  durationMs: DurationMs | null;
}

export const validCiJob = (input: unknown): CiJob => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["pipeline"] === undefined) throw new Error('`_x["pipeline"]` required but not present');
  
  if (_x["derivationPath"] === undefined) throw new Error('`_x["derivationPath"]` required but not present');
  
  if (_x["drvHash"] === undefined) throw new Error('`_x["drvHash"]` required but not present');
  
  if (_x["outputAttrPath"] === undefined) throw new Error('`_x["outputAttrPath"]` required but not present');
  
  if (_x["nixSystem"] === undefined) throw new Error('`_x["nixSystem"]` required but not present');
  _x["nixSystem"] === null || validNixSystem(_x["nixSystem"] as any);
  if (_x["outputType"] === undefined) throw new Error('`_x["outputType"]` required but not present');
  _x["outputType"] === null || validFlakeOutputType(_x["outputType"] as any);
  if (_x["status"] === undefined) throw new Error('`_x["status"]` required but not present');
  validJobStatus(_x["status"] as any);
  // handle html checkbox bs
  if(_x["isFlaky"] === undefined){
    _x["isFlaky"] = false;
  }else if((_x["isFlaky"] as unknown) === 'on'){
    _x["isFlaky"] = true;
  }
  if (_x["isFlaky"] === undefined) throw new Error('`_x["isFlaky"]` required but not present');
  if (typeof _x["isFlaky"] != 'boolean') throw new Error('`_x["isFlaky"]` must be a boolean');
  if (_x["storePath"] === undefined) throw new Error('`_x["storePath"]` required but not present');
  
  // handle html checkbox bs
  if(_x["cacheHit"] === undefined){
    _x["cacheHit"] = false;
  }else if((_x["cacheHit"] as unknown) === 'on'){
    _x["cacheHit"] = true;
  }
  if (_x["cacheHit"] === undefined) throw new Error('`_x["cacheHit"]` required but not present');
  if (typeof _x["cacheHit"] != 'boolean') throw new Error('`_x["cacheHit"]` must be a boolean');
  if (_x["closureSize"] === undefined) throw new Error('`_x["closureSize"]` required but not present');
  _x["closureSize"] === null || validNatural(unwrapNatural(unwrapClosureSize(_x["closureSize"] as any)) as any);
  // handle html checkbox bs
  if(_x["isUserOutput"] === undefined){
    _x["isUserOutput"] = false;
  }else if((_x["isUserOutput"] as unknown) === 'on'){
    _x["isUserOutput"] = true;
  }
  if (_x["isUserOutput"] === undefined) throw new Error('`_x["isUserOutput"]` required but not present');
  if (typeof _x["isUserOutput"] != 'boolean') throw new Error('`_x["isUserOutput"]` must be a boolean');
  if (_x["durationMs"] === undefined) throw new Error('`_x["durationMs"]` required but not present');
  _x["durationMs"] === null || validNatural(unwrapNatural(unwrapDurationMs(_x["durationMs"] as any)) as any);
  const x = input as CiJob;
  return x;
}
export const isValidCiJob = (input: unknown): boolean => {
  try { validCiJob(input); return true; } catch { return false; }
}

export interface JobDependency {
  upstream: Key<CiJob>;
  downstream: Key<CiJob>;
}

export const validJobDependency = (input: unknown): JobDependency => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["upstream"] === undefined) throw new Error('`_x["upstream"]` required but not present');
  
  if (_x["downstream"] === undefined) throw new Error('`_x["downstream"]` required but not present');
  
  const x = input as JobDependency;
  return x;
}
export const isValidJobDependency = (input: unknown): boolean => {
  try { validJobDependency(input); return true; } catch { return false; }
}

export interface JobAttempt {
  job: Key<CiJob>;
  attemptNumber: AttemptNumber;
  status: AttemptStatus;
  failureClass: FailureClass | null;
  exitCode: ExitCode | null;
  log: BuildLog | null;
  durationMs: DurationMs | null;
}

export const validJobAttempt = (input: unknown): JobAttempt => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["job"] === undefined) throw new Error('`_x["job"]` required but not present');
  
  if (_x["attemptNumber"] === undefined) throw new Error('`_x["attemptNumber"]` required but not present');
  validPositive(unwrapPositive(unwrapAttemptNumber(_x["attemptNumber"] as any)) as any);
  if (_x["status"] === undefined) throw new Error('`_x["status"]` required but not present');
  validAttemptStatus(_x["status"] as any);
  if (_x["failureClass"] === undefined) throw new Error('`_x["failureClass"]` required but not present');
  _x["failureClass"] === null || validFailureClass(_x["failureClass"] as any);
  if (_x["exitCode"] === undefined) throw new Error('`_x["exitCode"]` required but not present');
  _x["exitCode"] === null || validExitCode(_x["exitCode"] as any);
  if (_x["log"] === undefined) throw new Error('`_x["log"]` required but not present');
  
  if (_x["durationMs"] === undefined) throw new Error('`_x["durationMs"]` required but not present');
  _x["durationMs"] === null || validNatural(unwrapNatural(unwrapDurationMs(_x["durationMs"] as any)) as any);
  const x = input as JobAttempt;
  return x;
}
export const isValidJobAttempt = (input: unknown): boolean => {
  try { validJobAttempt(input); return true; } catch { return false; }
}

export interface Evaluation {
  pipeline: Key<Pipeline>;
  status: EvalStatus;
  durationMs: DurationMs | null;
  errorOutput: ErrorOutput | null;
  outputTree: OutputTree | null;
}

export const validEvaluation = (input: unknown): Evaluation => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["pipeline"] === undefined) throw new Error('`_x["pipeline"]` required but not present');
  
  if (_x["status"] === undefined) throw new Error('`_x["status"]` required but not present');
  validEvalStatus(_x["status"] as any);
  if (_x["durationMs"] === undefined) throw new Error('`_x["durationMs"]` required but not present');
  _x["durationMs"] === null || validNatural(unwrapNatural(unwrapDurationMs(_x["durationMs"] as any)) as any);
  if (_x["errorOutput"] === undefined) throw new Error('`_x["errorOutput"]` required but not present');
  
  if (_x["outputTree"] === undefined) throw new Error('`_x["outputTree"]` required but not present');
  
  const x = input as Evaluation;
  return x;
}
export const isValidEvaluation = (input: unknown): boolean => {
  try { validEvaluation(input); return true; } catch { return false; }
}

export interface FlakeInput {
  pipeline: Key<Pipeline>;
  name: FlakeInputName;
  url: Url | null;
  rev: GitRev | null;
  narHash: NarHash | null;
  changedSinceLast: boolean;
}

export const validFlakeInput = (input: unknown): FlakeInput => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["pipeline"] === undefined) throw new Error('`_x["pipeline"]` required but not present');
  
  if (_x["name"] === undefined) throw new Error('`_x["name"]` required but not present');
  
  if (_x["url"] === undefined) throw new Error('`_x["url"]` required but not present');
  
  if (_x["rev"] === undefined) throw new Error('`_x["rev"]` required but not present');
  
  if (_x["narHash"] === undefined) throw new Error('`_x["narHash"]` required but not present');
  
  // handle html checkbox bs
  if(_x["changedSinceLast"] === undefined){
    _x["changedSinceLast"] = false;
  }else if((_x["changedSinceLast"] as unknown) === 'on'){
    _x["changedSinceLast"] = true;
  }
  if (_x["changedSinceLast"] === undefined) throw new Error('`_x["changedSinceLast"]` required but not present');
  if (typeof _x["changedSinceLast"] != 'boolean') throw new Error('`_x["changedSinceLast"]` must be a boolean');
  const x = input as FlakeInput;
  return x;
}
export const isValidFlakeInput = (input: unknown): boolean => {
  try { validFlakeInput(input); return true; } catch { return false; }
}

export interface ScatterPoint {
  t: number;
  d: number;
  s: JobStatus;
}

export const validScatterPoint = (input: unknown): ScatterPoint => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["t"] === undefined) throw new Error('`_x["t"]` required but not present');
  if (typeof _x["t"] !== 'number' || !Number.isSafeInteger(_x["t"] as number)) throw new Error('`_x["t"]` must be a safe integer');
  if (_x["d"] === undefined) throw new Error('`_x["d"]` required but not present');
  if (typeof _x["d"] !== 'number' || !Number.isSafeInteger(_x["d"] as number)) throw new Error('`_x["d"]` must be a safe integer');
  if (_x["s"] === undefined) throw new Error('`_x["s"]` required but not present');
  validJobStatus(_x["s"] as any);
  const x = input as ScatterPoint;
  return x;
}
export const isValidScatterPoint = (input: unknown): boolean => {
  try { validScatterPoint(input); return true; } catch { return false; }
}

export interface RegressionLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface KdeData {
  grid: number[];
  densities: number[];
}

export const validKdeData = (input: unknown): KdeData => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["grid"] === undefined) throw new Error('`_x["grid"]` required but not present');
    if(_x["grid"] instanceof Array){
    if((_x["grid"] as any)?.length){
      (_x["grid"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  if (typeof y !== 'number' || !Number.isSafeInteger(y as number)) throw new Error('`y` must be a safe integer');
      });
    }else{
      _x["grid"] = _x["grid"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["grid"]` must be an Array');
  }
  if (_x["densities"] === undefined) throw new Error('`_x["densities"]` required but not present');
    if(_x["densities"] instanceof Array){
    if((_x["densities"] as any)?.length){
      (_x["densities"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  if (typeof y !== 'number' || !Number.isSafeInteger(y as number)) throw new Error('`y` must be a safe integer');
      });
    }else{
      _x["densities"] = _x["densities"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["densities"]` must be an Array');
  }
  const x = input as KdeData;
  return x;
}
export const isValidKdeData = (input: unknown): boolean => {
  try { validKdeData(input); return true; } catch { return false; }
}

export type DispatchKind = "runner" | "envHost"

export const validDispatchKind = (x: unknown): DispatchKind => {
  const v = x as [string, unknown] | string;
  if(((v != 'runner')) && ((v != 'envHost'))) throw new Error('Invalid DispatchKind `' + JSON.stringify(x) + '`');
  return x as DispatchKind;
}

export interface DispatchCandidate {
  targetId: BigNatural;
  score: number;
  breakdown: string;
  warnings: string | null;
}

export const validDispatchCandidate = (input: unknown): DispatchCandidate => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["targetId"] === undefined) throw new Error('`_x["targetId"]` required but not present');
  validBigNatural(_x["targetId"] as any);
  if (_x["score"] === undefined) throw new Error('`_x["score"]` required but not present');
  if (typeof _x["score"] != 'number') throw new Error('`_x["score"]` must be a number');
  if (_x["breakdown"] === undefined) throw new Error('`_x["breakdown"]` required but not present');
  if (typeof _x["breakdown"] != 'string') throw new Error('`_x["breakdown"]` must be a string');
  if (_x["warnings"] === undefined) throw new Error('`_x["warnings"]` required but not present');
  if (typeof _x["warnings"] != 'string' && _x["warnings"] != null) throw new Error('`_x["warnings"]` must be a string | null');
  const x = input as DispatchCandidate;
  return x;
}
export const isValidDispatchCandidate = (input: unknown): boolean => {
  try { validDispatchCandidate(input); return true; } catch { return false; }
}

export interface DispatchEvent {
  kind: DispatchKind;
  actorUser: Key<User> | null;
  repo: Key<Repo>;
  ref: string;
  devshellAttrPath: string | null;
  requestedArch: NixSystem;
  candidates: DispatchCandidate[];
  chosenEnvHost: Key<EnvHost> | null;
  chosenRunner: Key<Runner> | null;
  chosenExplicitly: boolean;
  dispatchedAt: DispatchedAt;
}

export const validDispatchEvent = (input: unknown): DispatchEvent => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["kind"] === undefined) throw new Error('`_x["kind"]` required but not present');
  validDispatchKind(_x["kind"] as any);
  if (_x["actorUser"] === undefined) throw new Error('`_x["actorUser"]` required but not present');
  
  if (_x["repo"] === undefined) throw new Error('`_x["repo"]` required but not present');
  
  if (_x["ref"] === undefined) throw new Error('`_x["ref"]` required but not present');
  if (typeof _x["ref"] != 'string') throw new Error('`_x["ref"]` must be a string');
  if (_x["devshellAttrPath"] === undefined) throw new Error('`_x["devshellAttrPath"]` required but not present');
  if (typeof _x["devshellAttrPath"] != 'string' && _x["devshellAttrPath"] != null) throw new Error('`_x["devshellAttrPath"]` must be a string | null');
  if (_x["requestedArch"] === undefined) throw new Error('`_x["requestedArch"]` required but not present');
  validNixSystem(_x["requestedArch"] as any);
  if (_x["candidates"] === undefined) throw new Error('`_x["candidates"]` required but not present');
    if(_x["candidates"] instanceof Array){
    if((_x["candidates"] as any)?.length){
      (_x["candidates"] as any).forEach((y: unknown) => {
         
  if (y === undefined) throw new Error('`y` required but not present');
  validDispatchCandidate(y as any);
      });
    }else{
      _x["candidates"] = _x["candidates"] === null ? null : [];
    }
  }else {
    throw new Error ('`_x["candidates"]` must be an Array');
  }
  if (_x["chosenEnvHost"] === undefined) throw new Error('`_x["chosenEnvHost"]` required but not present');
  
  if (_x["chosenRunner"] === undefined) throw new Error('`_x["chosenRunner"]` required but not present');
  
  // handle html checkbox bs
  if(_x["chosenExplicitly"] === undefined){
    _x["chosenExplicitly"] = false;
  }else if((_x["chosenExplicitly"] as unknown) === 'on'){
    _x["chosenExplicitly"] = true;
  }
  if (_x["chosenExplicitly"] === undefined) throw new Error('`_x["chosenExplicitly"]` required but not present');
  if (typeof _x["chosenExplicitly"] != 'boolean') throw new Error('`_x["chosenExplicitly"]` must be a boolean');
  if (_x["dispatchedAt"] === undefined) throw new Error('`_x["dispatchedAt"]` required but not present');
  validBigNatural(unwrapBigNatural(unwrapDispatchedAt(_x["dispatchedAt"] as any)) as any);
  const x = input as DispatchEvent;
  return x;
}
export const isValidDispatchEvent = (input: unknown): boolean => {
  try { validDispatchEvent(input); return true; } catch { return false; }
}

export const illuminateEnvHost = (x: EnvHost): string => unwrapEnvHostName(x.name)

export const illuminateEnvHostSession = (x: EnvHostSession): string => unwrapEnvSessionIdWire(x.sessionId)

export const illuminateSessionMetricsArchive = (x: SessionMetricsArchive): string => String(unwrapBigNatural(unwrapStartedAt(x.archivedAt)))

export const validTerminalDimension = (x: number): TerminalDimension => {
  if (typeof x !== 'number') throw new Error('Expected number but got ' + typeof x);
  if ((x) <= (0)) throw new Error(`Terminal dimension must be at least one cell`);
  if ((x) > (65535)) throw new Error(`Terminal dimension must fit an unsigned short`);
  return wrapTerminalDimension(x);
}
export const isValidTerminalDimension = (x: number): boolean => {
  try { validTerminalDimension(x); return true; } catch { return false; }
}

export const validSessionMemoryPercent = (x: number): SessionMemoryPercent => {
  if (typeof x !== 'number') throw new Error('Expected number but got ' + typeof x);
  if ((x) <= (0)) throw new Error(`Session memory percentage must be positive`);
  if ((x) > (100)) throw new Error(`Session memory percentage cannot exceed the host's RAM`);
  return wrapSessionMemoryPercent(x);
}
export const isValidSessionMemoryPercent = (x: number): boolean => {
  try { validSessionMemoryPercent(x); return true; } catch { return false; }
}

export const illuminateWorkspaceTemplate = (x: WorkspaceTemplate): string => x.name

export const illuminateWorkspacePolicy = (x: WorkspacePolicy): string => x.name

export const illuminateStatusColor = (__x: StatusColor): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "green400": {
  return "74, 222, 128";
  }
  case "red400": {
  return "248, 113, 113";
  }
  case "cyan400": {
  return "34, 211, 238";
  }
  case "amber400": {
  return "251, 191, 36";
  }
  case "zinc400": {
  return "161, 161, 170";
  }
  case "zinc500": {
  return "113, 113, 122";
  }
  }
}

export const illuminateScopeMode = (__x: ScopeMode): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "system": {
  return "system";
  }
  case "user": {
  return "user";
  }
  }
}

export const validLoadIntervalMs = (x: number): LoadIntervalMs => {
  if (typeof x !== 'number') throw new Error('Expected number but got ' + typeof x);
  if ((x) <= (0)) throw new Error(`Load interval must be positive milliseconds`);
  return wrapLoadIntervalMs(x);
}
export const isValidLoadIntervalMs = (x: number): boolean => {
  try { validLoadIntervalMs(x); return true; } catch { return false; }
}

export const validBackoffInitialMs = (x: number): BackoffInitialMs => {
  if (typeof x !== 'number') throw new Error('Expected number but got ' + typeof x);
  if ((x) <= (0)) throw new Error(`Backoff initial must be positive milliseconds`);
  return wrapBackoffInitialMs(x);
}
export const isValidBackoffInitialMs = (x: number): boolean => {
  try { validBackoffInitialMs(x); return true; } catch { return false; }
}

export const validBackoffMaxMs = (x: number): BackoffMaxMs => {
  if (typeof x !== 'number') throw new Error('Expected number but got ' + typeof x);
  if ((x) <= (0)) throw new Error(`Backoff max must be positive milliseconds`);
  return wrapBackoffMaxMs(x);
}
export const isValidBackoffMaxMs = (x: number): boolean => {
  try { validBackoffMaxMs(x); return true; } catch { return false; }
}

export const illuminateNixStoreMode = (__x: NixStoreMode): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "daemon": {
  return "daemon";
  }
  case "chroot": {
  return "chroot";
  }
  }
}

export const illuminateRunner = (x: Runner): string => unwrapRunnerName(x.name)

export const illuminateDispatchMode = (__x: DispatchMode): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "heuristic": {
  return "Heuristic";
  }
  case "bandit": {
  return "Bandit";
  }
  }
}

export const illuminateBanditDispatch = (x: BanditDispatch): string => String(unwrapBigNatural(unwrapDispatchedAt(x.dispatchedAt)))

export const illuminateBanditSnapshot = (x: BanditSnapshot): string => String(unwrapBigNatural(unwrapOutcomeAt(x.eventsThrough)))

export const illuminateJobHistory = (x: JobHistory): string => String(unwrapBigNatural(unwrapStartedAt(x.startedAt)))

export const illuminateEvalKind = (__x: EvalKind): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "pipelineEval": {
  return "Pipeline eval";
  }
  case "dispatchEval": {
  return "Dispatch eval";
  }
  case "appEval": {
  return "App eval";
  }
  case "moduleEval": {
  return "Module eval";
  }
  case "templateEval": {
  return "Template eval";
  }
  case "refBuild": {
  return "Ref build";
  }
  case "flakeCheck": {
  return "Flake check";
  }
  }
}

export const illuminatePipelineStatus = (__x: PipelineStatus): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "pending": {
  return "Pending";
  }
  case "evaluationPending": {
  return "Waiting for Runner";
  }
  case "evaluating": {
  return "Evaluating";
  }
  case "queued": {
  return "Queued";
  }
  case "building": {
  return "Building";
  }
  case "succeeded": {
  return "Succeeded";
  }
  case "failed": {
  return "Failed";
  }
  case "cancelled": {
  return "Cancelled";
  }
  }
}

export const illuminateJobStatus = (__x: JobStatus): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "queued": {
  return "Queued";
  }
  case "evaluating": {
  return "Evaluating";
  }
  case "preparing": {
  return "Preparing";
  }
  case "building": {
  return "Building";
  }
  case "retrying": {
  return "Retrying";
  }
  case "cached": {
  return "Cached";
  }
  case "succeeded": {
  return "Succeeded";
  }
  case "failed": {
  return "Failed";
  }
  case "cancelled": {
  return "Cancelled";
  }
  }
}

export const illuminateEvaluationPhase = (__x: EvaluationPhase): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "pending": {
  return "Pending";
  }
  case "evaluationPending": {
  return "Waiting for Runner";
  }
  case "evaluating": {
  return "Evaluating";
  }
  case "evaluated": {
  return "Evaluated";
  }
  case "evalFailed": {
  return "Evaluation Failed";
  }
  case "cancelled": {
  return "Cancelled";
  }
  }
}

export const illuminatePipelineFailureReason = (__x: PipelineFailureReason): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "noRunnerForSystem": {
  return "No runner online for required system";
  }
  case "noRunnerCapableOfSystem": {
  return "No runner capable of required system";
  }
  case "noRunnerAtEvalTime": {
  return "No runner online when evaluation started";
  }
  case "nixEvalFailed": {
  return "Nix eval failed";
  }
  case "evalRunnerDisconnected": {
  return "Runner disconnected during evaluation";
  }
  }
}

export const illuminateFailureClass = (__x: FailureClass): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "buildFailure": {
  return "Build Failure";
  }
  case "transientFailure": {
  return "Transient Failure";
  }
  case "evaluationFailure": {
  return "Evaluation Failure";
  }
  case "timeout": {
  return "Timeout";
  }
  case "infrastructureError": {
  return "Infrastructure Error";
  }
  case "unknownFailure": {
  return "Unknown Failure";
  }
  }
}

export const illuminateRetryBackoff = (__x: RetryBackoff): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "exponential": {
  return "Exponential";
  }
  case "linear": {
  return "Linear";
  }
  case "fixed": {
  return "Fixed";
  }
  }
}

export const illuminateFlakeOutputType = (__x: FlakeOutputType): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "check": {
  return "Check";
  }
  case "package": {
  return "Package";
  }
  case "devShell": {
  return "Dev Shell";
  }
  case "nixosConfiguration": {
  return "NixOS Configuration";
  }
  case "darwinConfiguration": {
  return "Darwin Configuration";
  }
  case "hydraJob": {
  return "Hydra Job";
  }
  case "custom": {
  return "Custom";
  }
  case "app": {
  return "App";
  }
  case "flakeCheck": {
  return "Flake Check";
  }
  case "dispatch": {
  return "Dispatch";
  }
  case "formatter": {
  return "Formatter";
  }
  case "template": {
  return "Template";
  }
  case "module": {
  return "Module";
  }
  }
}

export const illuminateNixSystem = (__x: NixSystem): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "amdLinux": {
  return "x86_64-linux";
  }
  case "armLinux": {
  return "aarch64-linux";
  }
  case "armDarwin": {
  return "aarch64-darwin";
  }
  case "amdDarwin": {
  return "x86_64-darwin";
  }
  }
}

export const illuminateAttemptStatus = (__x: AttemptStatus): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "running": {
  return "Running";
  }
  case "succeeded": {
  return "Succeeded";
  }
  case "failed": {
  return "Failed";
  }
  case "timedOut": {
  return "Timed Out";
  }
  case "cancelled": {
  return "Cancelled";
  }
  }
}

export const illuminateEvalStatus = (__x: EvalStatus): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "pending": {
  return "Pending";
  }
  case "running": {
  return "Running";
  }
  case "succeeded": {
  return "Succeeded";
  }
  case "failed": {
  return "Failed";
  }
  }
}

export const illuminatePipeline = (x: Pipeline): string => unwrapCommitSha(x.commitSha)

export const validPipeline = (input: unknown): Pipeline => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["repo"] === undefined) throw new Error('`_x["repo"]` required but not present');
  
  if (_x["commitSha"] === undefined) throw new Error('`_x["commitSha"]` required but not present');
  
  if (_x["branch"] === undefined) throw new Error('`_x["branch"]` required but not present');
  
  if (_x["evaluationPhase"] === undefined) throw new Error('`_x["evaluationPhase"]` required but not present');
  validEvaluationPhase(_x["evaluationPhase"] as any);
  if (_x["status"] === undefined) throw new Error('`_x["status"]` required but not present');
  validPipelineStatus(_x["status"] as any);
  if (_x["failureReason"] === undefined) throw new Error('`_x["failureReason"]` required but not present');
  _x["failureReason"] === null || validPipelineFailureReason(_x["failureReason"] as any);
  if (_x["flakeLockHash"] === undefined) throw new Error('`_x["flakeLockHash"]` required but not present');
  
  if (_x["retryMaxAttempts"] === undefined) throw new Error('`_x["retryMaxAttempts"]` required but not present');
  validPositive(unwrapPositive(unwrapRetryMaxAttempts(_x["retryMaxAttempts"] as any)) as any);
  if (_x["retryBaseDelayMs"] === undefined) throw new Error('`_x["retryBaseDelayMs"]` required but not present');
  validPositive(unwrapPositive(unwrapRetryBaseDelayMs(_x["retryBaseDelayMs"] as any)) as any);
  if (_x["retryBackoff"] === undefined) throw new Error('`_x["retryBackoff"]` required but not present');
  validRetryBackoff(_x["retryBackoff"] as any);
  if (_x["totalJobs"] === undefined) throw new Error('`_x["totalJobs"]` required but not present');
  validNatural(unwrapNatural(unwrapJobCount(_x["totalJobs"] as any)) as any);
  if (_x["cachedJobs"] === undefined) throw new Error('`_x["cachedJobs"]` required but not present');
  validNatural(unwrapNatural(unwrapJobCount(_x["cachedJobs"] as any)) as any);
  if (_x["builtJobs"] === undefined) throw new Error('`_x["builtJobs"]` required but not present');
  validNatural(unwrapNatural(unwrapJobCount(_x["builtJobs"] as any)) as any);
  if (_x["failedJobs"] === undefined) throw new Error('`_x["failedJobs"]` required but not present');
  validNatural(unwrapNatural(unwrapJobCount(_x["failedJobs"] as any)) as any);
  if (_x["retryCount"] === undefined) throw new Error('`_x["retryCount"]` required but not present');
  validNatural(unwrapNatural(unwrapRetryCount(_x["retryCount"] as any)) as any);
  if (_x["flakyJobCount"] === undefined) throw new Error('`_x["flakyJobCount"]` required but not present');
  validNatural(unwrapNatural(unwrapJobCount(_x["flakyJobCount"] as any)) as any);
  if (_x["wallClockDurationMs"] === undefined) throw new Error('`_x["wallClockDurationMs"]` required but not present');
  _x["wallClockDurationMs"] === null || validNatural(unwrapNatural(unwrapDurationMs(_x["wallClockDurationMs"] as any)) as any);
  if (_x["sumJobDurationMs"] === undefined) throw new Error('`_x["sumJobDurationMs"]` required but not present');
  _x["sumJobDurationMs"] === null || validNatural(unwrapNatural(unwrapDurationMs(_x["sumJobDurationMs"] as any)) as any);
  const x = input as Pipeline;
  const errors: string[] = [];
  if ((unwrapNatural(unwrapPositive(unwrapRetryBaseDelayMs(x.retryBaseDelayMs)))) < 1000) errors.push(`Base delay must be at least 1 second`);
  if ((unwrapNatural(unwrapPositive(unwrapRetryMaxAttempts(x.retryMaxAttempts)))) > 10) errors.push(`Max attempts cannot exceed 10`);
  if (errors.length > 0) throw new Error(errors.join('; '));
  return x;
}
export const isValidPipeline = (input: unknown): boolean => {
  try { validPipeline(input); return true; } catch { return false; }
}

export const illuminateCiJob = (x: CiJob): string => ((v) => v == null ? "" : unwrapAttrPath(v))(x.outputAttrPath)

export const illuminateJobAttempt = (x: JobAttempt): string => String(unwrapNatural(unwrapPositive(unwrapAttemptNumber(x.attemptNumber))))

export const concealJobAttempt = (x: JobAttempt) => ((v) => v == null ? "" : unwrapBuildLog(v))(x.log)

export const illuminateEvaluation = (x: Evaluation): string => illuminateEvalStatus(x.status)

export const concealEvaluation = (x: Evaluation) => ((v) => v == null ? "" : unwrapErrorOutput(v))(x.errorOutput) + ((v) => v == null ? "" : unwrapOutputTree(v))(x.outputTree)

export const illuminateFlakeInput = (x: FlakeInput): string => unwrapFlakeInputName(x.name)

export const validRegressionLine = (input: unknown): RegressionLine => {
  const _x = input as globalThis.Record<string, unknown>;
  if (_x["x1"] === undefined) throw new Error('`_x["x1"]` required but not present');
  if (typeof _x["x1"] !== 'number' || !Number.isSafeInteger(_x["x1"] as number)) throw new Error('`_x["x1"]` must be a safe integer');
  if (_x["y1"] === undefined) throw new Error('`_x["y1"]` required but not present');
  if (typeof _x["y1"] !== 'number' || !Number.isSafeInteger(_x["y1"] as number)) throw new Error('`_x["y1"]` must be a safe integer');
  if (_x["x2"] === undefined) throw new Error('`_x["x2"]` required but not present');
  if (typeof _x["x2"] !== 'number' || !Number.isSafeInteger(_x["x2"] as number)) throw new Error('`_x["x2"]` must be a safe integer');
  if (_x["y2"] === undefined) throw new Error('`_x["y2"]` required but not present');
  if (typeof _x["y2"] !== 'number' || !Number.isSafeInteger(_x["y2"] as number)) throw new Error('`_x["y2"]` must be a safe integer');
  const x = input as RegressionLine;
  const errors: string[] = [];
  if ((x.x1) < 0) errors.push(`Regression line x1 must be non-negative epoch ms`);
  if ((x.x2) < 0) errors.push(`Regression line x2 must be non-negative epoch ms`);
  if (errors.length > 0) throw new Error(errors.join('; '));
  return x;
}
export const isValidRegressionLine = (input: unknown): boolean => {
  try { validRegressionLine(input); return true; } catch { return false; }
}

export const illuminateDispatchKind = (__x: DispatchKind): string => {
  const tag = typeof __x === 'string' ? __x : __x[0];
  switch (tag) {
  case "runner": {
  return "Runner";
  }
  case "envHost": {
  return "Env Host";
  }
  }
}

export const illuminateDispatchEvent = (x: DispatchEvent): string => String(unwrapBigNatural(unwrapDispatchedAt(x.dispatchedAt)))

export const envListDevShellsRpcTimeoutMs: number =
  60000


export const envFsRpcTimeoutMs: number =
  20000


export const envCommitRpcTimeoutMs: number =
  90000


export const defaultSessionMemoryPercent: SessionMemoryPercent =
  wrapSessionMemoryPercent(75)


export const createEnvHost: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/env-hosts",
    'query': null,
    'fragment': null,
    'io': 'EnvHost. EnvHost',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listEnvHosts: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/env-hosts",
    'query': null,
    'fragment': null,
    'io': 'Unit. [EnvHost]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createEnvHostSession: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/env-host-sessions",
    'query': null,
    'fragment': null,
    'io': 'EnvHostSession. EnvHostSession',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listEnvHostSessions: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/env-host-sessions",
    'query': null,
    'fragment': null,
    'io': 'Unit. [EnvHostSession]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createSessionMetricsArchive: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/session-metrics-archives",
    'query': null,
    'fragment': null,
    'io': 'SessionMetricsArchive. SessionMetricsArchive',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listSessionMetricsArchives: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/session-metrics-archives",
    'query': null,
    'fragment': null,
    'io': 'Unit. [SessionMetricsArchive]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createWorkspaceTemplate: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/workspace-templates",
    'query': null,
    'fragment': null,
    'io': 'WorkspaceTemplate. WorkspaceTemplate',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listWorkspaceTemplates: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/workspace-templates",
    'query': null,
    'fragment': null,
    'io': 'Unit. [WorkspaceTemplate]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createWorkspacePolicy: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/workspace-policies",
    'query': null,
    'fragment': null,
    'io': 'WorkspacePolicy. WorkspacePolicy',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listWorkspacePolicies: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/workspace-policies",
    'query': null,
    'fragment': null,
    'io': 'Unit. [WorkspacePolicy]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const defaultEvaluationPhaseContext: EvaluationPhaseContext =
  {
    'placeholder': 0,
    'zero': 0,
  }


export const allNixSystems: Set<NixSystem> =
  new Set(['amdLinux', 'armLinux', 'amdDarwin', 'armDarwin'])


export const exemplarPipelineStatus: PipelineStatus =
  'succeeded'


export const exemplarJobStatus: JobStatus =
  'succeeded'


export const allJobStatuses: Set<JobStatus> =
  new Set(['queued', 'evaluating', 'preparing', 'building', 'retrying', 'cached', 'succeeded', 'failed', 'cancelled'])


export const activeJobStatuses: Set<JobStatus> =
  new Set(['evaluating', 'preparing', 'building', 'retrying'])


export const allActiveJobStatuses: Set<JobStatus> =
  new Set(['evaluating', 'preparing', 'building', 'retrying'])


export const successfulTerminalJobStatuses: Set<JobStatus> =
  new Set(['succeeded', 'cached'])


export const runnerActiveJobStatuses: Set<JobStatus> =
  new Set(['preparing', 'building', 'retrying'])


export const terminalJobStatuses: Set<JobStatus> =
  new Set(['cached', 'succeeded', 'cancelled'])


export const allTerminalJobStatuses: Set<JobStatus> =
  new Set(['cached', 'succeeded', 'cancelled'])


export const uiTerminalJobStatuses: Set<JobStatus> =
  new Set(['cached', 'succeeded', 'failed', 'cancelled'])


export const allPipelineStatuses: Set<PipelineStatus> =
  new Set(['pending', 'evaluationPending', 'evaluating', 'queued', 'building', 'succeeded', 'failed', 'cancelled'])


export const terminalPipelineStatuses: Set<PipelineStatus> =
  new Set(['succeeded', 'failed', 'cancelled'])


export const retryablePipelineStatuses: Set<PipelineStatus> =
  new Set(['failed', 'cancelled'])


export const failedPipelineStatuses: Set<PipelineStatus> =
  new Set(['failed'])


export const resultPipelineStatuses: Set<PipelineStatus> =
  new Set(['succeeded', 'failed'])


export const chartableTerminalPipelineStatuses: Set<PipelineStatus> =
  new Set(['succeeded', 'failed', 'cancelled'])


export const nonCompletedJobStatuses: Set<JobStatus> =
  new Set(['failed', 'cancelled', 'building', 'retrying', 'evaluating'])


export const inProgressEvaluationPhases: Set<EvaluationPhase> =
  new Set(['evaluationPending', 'evaluating'])


export const pipelineStatusHex = (status: PipelineStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "pending": {
        return "#a1a1aa";
      }
      case "evaluationPending": {
        return "#f59e0b";
      }
      case "evaluating": {
        return "#06b6d4";
      }
      case "queued": {
        return "#a1a1aa";
      }
      case "building": {
        return "#06b6d4";
      }
      case "succeeded": {
        return "#22c55e";
      }
      case "failed": {
        return "#ef4444";
      }
      case "cancelled": {
        return "#71717a";
      }
    default: throw new Error('unreachable');
    }})()
}

export const preEvaluationPhases: Set<EvaluationPhase> =
  new Set(['pending', 'evaluationPending', 'evaluating'])


export const jobStatusGlyph = (status: JobStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "queued": {
        return "f017";
      }
      case "evaluating": {
        return "f021";
      }
      case "preparing": {
        return "f251";
      }
      case "building": {
        return "f021";
      }
      case "retrying": {
        return "f021";
      }
      case "cached": {
        return "f187";
      }
      case "succeeded": {
        return "f058";
      }
      case "failed": {
        return "f057";
      }
      case "cancelled": {
        return "f05e";
      }
    default: throw new Error('unreachable');
    }})()
}

export const pipelineStatusGlyph = (status: PipelineStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "pending": {
        return "f017";
      }
      case "evaluationPending": {
        return "f252";
      }
      case "evaluating": {
        return "f021";
      }
      case "queued": {
        return "f017";
      }
      case "building": {
        return "f021";
      }
      case "succeeded": {
        return "f058";
      }
      case "failed": {
        return "f057";
      }
      case "cancelled": {
        return "f05e";
      }
    default: throw new Error('unreachable');
    }})()
}

export const pipelineStatusFavicon = (status: PipelineStatus): PipelineFavicon => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "pending": {
        return {
          'glyph': "f017",
          'hex': "#a1a1aa",
        };
      }
      case "evaluationPending": {
        return {
          'glyph': "f252",
          'hex': "#f59e0b",
        };
      }
      case "evaluating": {
        return {
          'glyph': "f021",
          'hex': "#06b6d4",
        };
      }
      case "queued": {
        return {
          'glyph': "f017",
          'hex': "#a1a1aa",
        };
      }
      case "building": {
        return {
          'glyph': "f021",
          'hex': "#06b6d4",
        };
      }
      case "succeeded": {
        return {
          'glyph': "f058",
          'hex': "#22c55e",
        };
      }
      case "failed": {
        return {
          'glyph': "f057",
          'hex': "#ef4444",
        };
      }
      case "cancelled": {
        return {
          'glyph': "f05e",
          'hex': "#71717a",
        };
      }
    default: throw new Error('unreachable');
    }})()
}

export const jobStatusLabel = (status: JobStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "queued": {
        return "Queued";
      }
      case "evaluating": {
        return "Evaluating";
      }
      case "preparing": {
        return "Preparing";
      }
      case "building": {
        return "Building";
      }
      case "retrying": {
        return "Retrying";
      }
      case "cached": {
        return "Cached";
      }
      case "succeeded": {
        return "Passed";
      }
      case "failed": {
        return "Failed";
      }
      case "cancelled": {
        return "Cancelled";
      }
    default: throw new Error('unreachable');
    }})()
}

export const jobStatusColorKey = (status: JobStatus): StatusColorKey => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "queued": {
        return 'muted';
      }
      case "evaluating": {
        return 'info';
      }
      case "preparing": {
        return 'info';
      }
      case "building": {
        return 'info';
      }
      case "retrying": {
        return 'warning';
      }
      case "cached": {
        return 'accentMuted';
      }
      case "succeeded": {
        return 'accent';
      }
      case "failed": {
        return 'danger';
      }
      case "cancelled": {
        return 'muted';
      }
    default: throw new Error('unreachable');
    }})()
}

export const jobStatusColor = (status: JobStatus): StatusColor => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "queued": {
        return 'zinc400';
      }
      case "evaluating": {
        return 'cyan400';
      }
      case "preparing": {
        return 'cyan400';
      }
      case "building": {
        return 'cyan400';
      }
      case "retrying": {
        return 'amber400';
      }
      case "cached": {
        return 'green400';
      }
      case "succeeded": {
        return 'green400';
      }
      case "failed": {
        return 'red400';
      }
      case "cancelled": {
        return 'zinc500';
      }
    default: throw new Error('unreachable');
    }})()
}

export const jobCacheLabel = (status: JobStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "queued": {
        return "Pending";
      }
      case "evaluating": {
        return "Pending";
      }
      case "preparing": {
        return "Pending";
      }
      case "building": {
        return "Miss (building)";
      }
      case "retrying": {
        return "Miss (building)";
      }
      case "cached": {
        return "Hit (substituted)";
      }
      case "succeeded": {
        return "Miss (built)";
      }
      case "failed": {
        return "Miss (built)";
      }
      case "cancelled": {
        return "N/A";
      }
    default: throw new Error('unreachable');
    }})()
}

export const pipelineStatusLabel = (status: PipelineStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "pending": {
        return "Pending";
      }
      case "evaluationPending": {
        return "Waiting for Runner";
      }
      case "evaluating": {
        return "Evaluating";
      }
      case "queued": {
        return "Queued";
      }
      case "building": {
        return "Building";
      }
      case "succeeded": {
        return "Passed";
      }
      case "failed": {
        return "Failed";
      }
      case "cancelled": {
        return "Cancelled";
      }
    default: throw new Error('unreachable');
    }})()
}

export const pipelineStatusColorKey = (status: PipelineStatus): StatusColorKey => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "pending": {
        return 'muted';
      }
      case "evaluationPending": {
        return 'warning';
      }
      case "evaluating": {
        return 'info';
      }
      case "queued": {
        return 'muted';
      }
      case "building": {
        return 'info';
      }
      case "succeeded": {
        return 'accent';
      }
      case "failed": {
        return 'danger';
      }
      case "cancelled": {
        return 'muted';
      }
    default: throw new Error('unreachable');
    }})()
}

export const pipelineStatusColor = (status: PipelineStatus): StatusColor => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "pending": {
        return 'zinc400';
      }
      case "evaluationPending": {
        return 'amber400';
      }
      case "evaluating": {
        return 'cyan400';
      }
      case "queued": {
        return 'zinc400';
      }
      case "building": {
        return 'cyan400';
      }
      case "succeeded": {
        return 'green400';
      }
      case "failed": {
        return 'red400';
      }
      case "cancelled": {
        return 'zinc500';
      }
    default: throw new Error('unreachable');
    }})()
}

export const defaultJobStatusContext: JobStatusContext =
  {
    'placeholder': 0,
    'zero': 0,
  }


export const defaultKeyFile: KeyFile =
  wrapKeyFile("/var/lib/runner/keys/id_ed25519")


export const defaultNixBin: NixBin =
  wrapNixBin("nix")


export const defaultSystemdRun: SystemdRunBin =
  wrapSystemdRunBin("systemd-run")


export const defaultScriptBin: ScriptBin =
  wrapScriptBin("script")


export const defaultLoadIntervalMs: LoadIntervalMs =
  wrapLoadIntervalMs(10000)


export const defaultBackoffInitialMs: BackoffInitialMs =
  wrapBackoffInitialMs(1000)


export const defaultBackoffMaxMs: BackoffMaxMs =
  wrapBackoffMaxMs(60000)


export const defaultNixStoreMode: NixStoreMode =
  'daemon'


export const defaultScopeMode: ScopeMode =
  'system'


export const serverPubkeyMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': "HEX",
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': "eitherReader (fmap ServerPubkeyHex . validateServerPubkeyHex)",
    'defaultValue': null,
  }


export const keyFileMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': null,
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': null,
    'defaultValue': "defaultKeyFile",
  }


export const nixBinMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': null,
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': null,
    'defaultValue': "defaultNixBin",
  }


export const systemdRunMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': null,
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': null,
    'defaultValue': "defaultSystemdRun",
  }


export const scriptBinMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': null,
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': null,
    'defaultValue': "defaultScriptBin",
  }


export const loadIntervalMsMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': null,
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': null,
    'defaultValue': "defaultLoadIntervalMs",
  }


export const backoffInitialMsMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': null,
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': null,
    'defaultValue': "defaultBackoffInitialMs",
  }


export const backoffMaxMsMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': null,
    'help': null,
    'hidden': null,
    'argument': null,
    'reader': null,
    'defaultValue': "defaultBackoffMaxMs",
  }


export const nixStoreModeMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': "MODE",
    'help': "How nix builds reach the store: 'daemon' (system nix-daemon, remote builders honoured, per-job CPU chart reads ~0%) or 'chroot' (in-process store under the runner's state dir, build runs inside the per-job cgroup so the chart reflects real activity).",
    'hidden': null,
    'argument': null,
    'reader': "eitherReader parseNixStoreMode",
    'defaultValue': "defaultNixStoreMode",
  }


export const scopeModeMeta: FieldMeta =
  {
    'short': null,
    'env': null,
    'conf': null,
    'metavar': "MODE",
    'help': "Which systemd instance owns the transient build scopes: 'system' (production; the runner is a system service and `systemd-run --scope` creates units in the system instance) or 'user' (dev; `nix run .#dev-runner` wrapper passes --user to systemd-run, scope lives in the calling user's systemd instance). The cancel path addresses whichever instance the scope was created in.",
    'hidden': null,
    'argument': null,
    'reader': "eitherReader parseScopeMode",
    'defaultValue': "defaultScopeMode",
  }


export const runnerCommand: CommandSpec =
  {
    'typeName': "RunnerConfig",
    'description': "Self-hosted CI build runner - dial out to operation, accept builds",
    'envPrefix': null,
    'fields': new Map([["serverPubkey", serverPubkeyMeta], ["keyFile", keyFileMeta], ["nixBin", nixBinMeta], ["systemdRun", systemdRunMeta], ["scriptBin", scriptBinMeta], ["nixStoreMode", nixStoreModeMeta], ["scopeMode", scopeModeMeta], ["loadIntervalMs", loadIntervalMsMeta], ["backoffInitialMs", backoffInitialMsMeta], ["backoffMaxMs", backoffMaxMsMeta]]),
    'subcommands': null,
    'imports': ["Dox.Ci", "Dox.Runtime", "ComputeHost.Common.CommandReaders"],
    'externalType': true,
  }


export const postEvalEvalKinds: Set<EvalKind> =
  new Set(['appEval', 'moduleEval', 'templateEval', 'refBuild', 'flakeCheck'])


export const failureClassShortLabel = (cls: FailureClass): string => {
  return (() => {switch (typeof cls === 'string' ? cls : (cls as [string, unknown])[0]) {
      case "buildFailure": {
        return "build error";
      }
      case "transientFailure": {
        return "transient";
      }
      case "evaluationFailure": {
        return "eval error";
      }
      case "timeout": {
        return "timeout";
      }
      case "infrastructureError": {
        return "infra error";
      }
      case "unknownFailure": {
        return "unknown";
      }
    default: throw new Error('unreachable');
    }})()
}

export const attemptStatusIconName = (status: AttemptStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "running": {
        return "pipeline_building";
      }
      case "succeeded": {
        return "pipeline_succeeded";
      }
      case "failed": {
        return "pipeline_failed";
      }
      case "timedOut": {
        return "pipeline_failed";
      }
      case "cancelled": {
        return "pipeline_cancelled";
      }
    default: throw new Error('unreachable');
    }})()
}

export const attemptStatusBadgeVariant = (status: AttemptStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "running": {
        return "info";
      }
      case "succeeded": {
        return "success";
      }
      case "failed": {
        return "danger";
      }
      case "timedOut": {
        return "warning";
      }
      case "cancelled": {
        return "default";
      }
    default: throw new Error('unreachable');
    }})()
}

export const attemptStatusVerb = (status: AttemptStatus): string => {
  return (() => {switch (typeof status === 'string' ? status : (status as [string, unknown])[0]) {
      case "running": {
        return "running";
      }
      case "succeeded": {
        return "passed";
      }
      case "failed": {
        return "failed";
      }
      case "timedOut": {
        return "timed out";
      }
      case "cancelled": {
        return "cancelled";
      }
    default: throw new Error('unreachable');
    }})()
}

export const dispatchKindBadgeVariant = (kind: DispatchKind): string => {
  return (() => {switch (typeof kind === 'string' ? kind : (kind as [string, unknown])[0]) {
      case "runner": {
        return "success";
      }
      case "envHost": {
        return "info";
      }
    default: throw new Error('unreachable');
    }})()
}

export const dispatchKindWireForm = (kind: DispatchKind): string => {
  return (() => {switch (typeof kind === 'string' ? kind : (kind as [string, unknown])[0]) {
      case "runner": {
        return "runner";
      }
      case "envHost": {
        return "env_host";
      }
    default: throw new Error('unreachable');
    }})()
}

export const dispatchKindRouteSegment = (kind: DispatchKind): string => {
  return (() => {switch (typeof kind === 'string' ? kind : (kind as [string, unknown])[0]) {
      case "runner": {
        return "runners";
      }
      case "envHost": {
        return "env-hosts";
      }
    default: throw new Error('unreachable');
    }})()
}

export const dispatchKindExplicitMode = (kind: DispatchKind): string => {
  return (() => {switch (typeof kind === 'string' ? kind : (kind as [string, unknown])[0]) {
      case "runner": {
        return "sticky";
      }
      case "envHost": {
        return "picker";
      }
    default: throw new Error('unreachable');
    }})()
}

export const dispatchKindAutoMode = (kind: DispatchKind): string => {
  return (() => {switch (typeof kind === 'string' ? kind : (kind as [string, unknown])[0]) {
      case "runner": {
        return "bandit";
      }
      case "envHost": {
        return "shim";
      }
    default: throw new Error('unreachable');
    }})()
}

export const allDispatchKinds: Set<DispatchKind> =
  new Set(['runner', 'envHost'])


export const createDispatchEvent: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/dispatch-events",
    'query': null,
    'fragment': null,
    'io': 'DispatchEvent. DispatchEvent',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listDispatchEvents: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/dispatch-events",
    'query': null,
    'fragment': null,
    'io': 'Unit. [DispatchEvent]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createPipeline: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/pipelines",
    'query': null,
    'fragment': null,
    'io': 'Pipeline. Pipeline',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listPipelines: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/pipelines",
    'query': null,
    'fragment': null,
    'io': 'Unit. [Pipeline]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createCiJob: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/ci-jobs",
    'query': null,
    'fragment': null,
    'io': 'CiJob. CiJob',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listCiJobs: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/ci-jobs",
    'query': null,
    'fragment': null,
    'io': 'Unit. [CiJob]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createJobDependency: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/job-dependencies",
    'query': null,
    'fragment': null,
    'io': 'JobDependency. JobDependency',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listJobDependencies: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/job-dependencies",
    'query': null,
    'fragment': null,
    'io': 'Unit. [JobDependency]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createJobAttempt: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/job-attempts",
    'query': null,
    'fragment': null,
    'io': 'JobAttempt. JobAttempt',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listJobAttempts: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/job-attempts",
    'query': null,
    'fragment': null,
    'io': 'Unit. [JobAttempt]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createEvaluation: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/evaluations",
    'query': null,
    'fragment': null,
    'io': 'Evaluation. Evaluation',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listEvaluations: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/evaluations",
    'query': null,
    'fragment': null,
    'io': 'Unit. [Evaluation]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createFlakeInput: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/flake-inputs",
    'query': null,
    'fragment': null,
    'io': 'FlakeInput. FlakeInput',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listFlakeInputs: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/flake-inputs",
    'query': null,
    'fragment': null,
    'io': 'Unit. [FlakeInput]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createRunner: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/runners",
    'query': null,
    'fragment': null,
    'io': 'Runner. Runner',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listRunners: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/runners",
    'query': null,
    'fragment': null,
    'io': 'Unit. [Runner]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createJobHistory: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/job-histories",
    'query': null,
    'fragment': null,
    'io': 'JobHistory. JobHistory',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listJobHistories: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/job-histories",
    'query': null,
    'fragment': null,
    'io': 'Unit. [JobHistory]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createBanditDispatch: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/bandit-dispatches",
    'query': null,
    'fragment': null,
    'io': 'BanditDispatch. BanditDispatch',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listBanditDispatches: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/bandit-dispatches",
    'query': null,
    'fragment': null,
    'io': 'Unit. [BanditDispatch]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


export const createBanditSnapshot: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/bandit-snapshots",
    'query': null,
    'fragment': null,
    'io': 'BanditSnapshot. BanditSnapshot',
    'meta': new Set(["POST"]),
    'accept': null,
  }


export const listBanditSnapshots: URI =
  {
    'scheme': "http",
    'authority': {
      'user': null,
      'name': "localhost",
      'port': null,
    },
    'path': "/bandit-snapshots",
    'query': null,
    'fragment': null,
    'io': 'Unit. [BanditSnapshot]',
    'meta': new Set(["GET"]),
    'accept': null,
  }


// Generated FSM: evaluationPhase

export type EvaluationPhaseTransitionResult = {
  target: EvaluationPhase;
  context: EvaluationPhaseContext;
};

export function evaluationPhaseTransition(
  state: EvaluationPhase,
  event: EvaluationEvent,
  context: EvaluationPhaseContext
): EvaluationPhaseTransitionResult {
  switch (state) {
    case "pending": {
      switch (event) {
        case "startEval":
          return { target: "evaluating", context };
        case "cancel":
          return { target: "cancelled", context };
        case "evalCompleted":
          return { target: "pending", context };
        case "evalFailed":
          return { target: "pending", context };
        case "noRunner":
          return { target: "evaluationPending", context };
        default:
          return { target: state, context };
      }
    }
    case "evaluationPending": {
      switch (event) {
        case "startEval":
          return { target: "evaluating", context };
        case "cancel":
          return { target: "cancelled", context };
        case "noRunner":
          return { target: "evalFailed", context };
        case "evalCompleted":
          return { target: "evaluationPending", context };
        case "evalFailed":
          return { target: "evaluationPending", context };
        default:
          return { target: state, context };
      }
    }
    case "evaluating": {
      switch (event) {
        case "evalCompleted":
          return { target: "evaluated", context };
        case "evalFailed":
          return { target: "evalFailed", context };
        case "cancel":
          return { target: "cancelled", context };
        case "startEval":
          return { target: "evaluating", context };
        case "noRunner":
          return { target: "evaluating", context };
        default:
          return { target: state, context };
      }
    }
    case "evaluated": {
      switch (event) {
        case "startEval":
          return { target: "evaluated", context };
        case "evalCompleted":
          return { target: "evaluated", context };
        case "evalFailed":
          return { target: "evaluated", context };
        case "cancel":
          return { target: "evaluated", context };
        case "noRunner":
          return { target: "evaluated", context };
        default:
          return { target: state, context };
      }
    }
    case "evalFailed": {
      switch (event) {
        case "startEval":
          return { target: "evalFailed", context };
        case "evalCompleted":
          return { target: "evalFailed", context };
        case "evalFailed":
          return { target: "evalFailed", context };
        case "cancel":
          return { target: "evalFailed", context };
        case "noRunner":
          return { target: "evalFailed", context };
        default:
          return { target: state, context };
      }
    }
    case "cancelled": {
      switch (event) {
        case "startEval":
          return { target: "cancelled", context };
        case "evalCompleted":
          return { target: "cancelled", context };
        case "evalFailed":
          return { target: "cancelled", context };
        case "cancel":
          return { target: "cancelled", context };
        case "noRunner":
          return { target: "cancelled", context };
        default:
          return { target: state, context };
      }
    }
    default:
      return { target: state, context };
  }
}

export function validTransitionEvaluationPhase(from: EvaluationPhase, event: EvaluationEvent): boolean {
  switch (from) {
    case "pending": {
      switch (event) {
        case "startEval":
          return true;
        case "cancel":
          return true;
        case "evalCompleted":
          return true;
        case "evalFailed":
          return true;
        case "noRunner":
          return true;
        default:
          return false;
      }
    }
    case "evaluationPending": {
      switch (event) {
        case "startEval":
          return true;
        case "cancel":
          return true;
        case "noRunner":
          return true;
        case "evalCompleted":
          return true;
        case "evalFailed":
          return true;
        default:
          return false;
      }
    }
    case "evaluating": {
      switch (event) {
        case "evalCompleted":
          return true;
        case "evalFailed":
          return true;
        case "cancel":
          return true;
        case "startEval":
          return true;
        case "noRunner":
          return true;
        default:
          return false;
      }
    }
    case "evaluated": {
      switch (event) {
        case "startEval":
          return true;
        case "evalCompleted":
          return true;
        case "evalFailed":
          return true;
        case "cancel":
          return true;
        case "noRunner":
          return true;
        default:
          return false;
      }
    }
    case "evalFailed": {
      switch (event) {
        case "startEval":
          return true;
        case "evalCompleted":
          return true;
        case "evalFailed":
          return true;
        case "cancel":
          return true;
        case "noRunner":
          return true;
        default:
          return false;
      }
    }
    case "cancelled": {
      switch (event) {
        case "startEval":
          return true;
        case "evalCompleted":
          return true;
        case "evalFailed":
          return true;
        case "cancel":
          return true;
        case "noRunner":
          return true;
        default:
          return false;
      }
    }
    default:
      return false;
  }
}

export function terminalEvaluationPhase(state: EvaluationPhase): boolean {
  switch (state) {
    case "cancelled":
      return true;
    case "evalFailed":
      return true;
    case "evaluated":
      return true;
    default:
      return false;
  }
}


// Generated FSM: jobStatus

export type JobStatusTransitionResult = {
  target: JobStatus;
  context: JobStatusContext;
};

export function jobStatusTransition(
  state: JobStatus,
  event: JobEvent,
  context: JobStatusContext
): JobStatusTransitionResult {
  switch (state) {
    case "queued": {
      switch (event) {
        case "startJobEval":
          return { target: "evaluating", context };
        case "cacheHit":
          return { target: "cached", context };
        case "cancelJob":
          return { target: "cancelled", context };
        case "jobEvalDone":
          return { target: "queued", context };
        case "startBuild":
          return { target: "preparing", context };
        case "buildSucceeded":
          return { target: "queued", context };
        case "buildFailed":
          return { target: "queued", context };
        case "startRetry":
          return { target: "queued", context };
        case "builderStarted":
          return { target: "queued", context };
        default:
          return { target: state, context };
      }
    }
    case "evaluating": {
      switch (event) {
        case "jobEvalDone":
          return { target: "preparing", context };
        case "cancelJob":
          return { target: "cancelled", context };
        case "startJobEval":
          return { target: "evaluating", context };
        case "startBuild":
          return { target: "evaluating", context };
        case "buildSucceeded":
          return { target: "evaluating", context };
        case "buildFailed":
          return { target: "evaluating", context };
        case "cacheHit":
          return { target: "evaluating", context };
        case "startRetry":
          return { target: "evaluating", context };
        case "builderStarted":
          return { target: "evaluating", context };
        default:
          return { target: state, context };
      }
    }
    case "preparing": {
      switch (event) {
        case "builderStarted":
          return { target: "building", context };
        case "buildFailed":
          return { target: "failed", context };
        case "cancelJob":
          return { target: "cancelled", context };
        case "startJobEval":
          return { target: "preparing", context };
        case "jobEvalDone":
          return { target: "preparing", context };
        case "startBuild":
          return { target: "preparing", context };
        case "buildSucceeded":
          return { target: "preparing", context };
        case "cacheHit":
          return { target: "preparing", context };
        case "startRetry":
          return { target: "preparing", context };
        default:
          return { target: state, context };
      }
    }
    case "building": {
      switch (event) {
        case "buildSucceeded":
          return { target: "succeeded", context };
        case "buildFailed":
          return { target: "failed", context };
        case "cancelJob":
          return { target: "cancelled", context };
        case "startJobEval":
          return { target: "building", context };
        case "jobEvalDone":
          return { target: "building", context };
        case "startBuild":
          return { target: "building", context };
        case "cacheHit":
          return { target: "building", context };
        case "startRetry":
          return { target: "building", context };
        case "builderStarted":
          return { target: "building", context };
        default:
          return { target: state, context };
      }
    }
    case "retrying": {
      switch (event) {
        case "startBuild":
          return { target: "preparing", context };
        case "cancelJob":
          return { target: "cancelled", context };
        case "startJobEval":
          return { target: "retrying", context };
        case "jobEvalDone":
          return { target: "retrying", context };
        case "buildSucceeded":
          return { target: "retrying", context };
        case "buildFailed":
          return { target: "retrying", context };
        case "cacheHit":
          return { target: "retrying", context };
        case "startRetry":
          return { target: "retrying", context };
        case "builderStarted":
          return { target: "retrying", context };
        default:
          return { target: state, context };
      }
    }
    case "cached": {
      switch (event) {
        case "startJobEval":
          return { target: "cached", context };
        case "jobEvalDone":
          return { target: "cached", context };
        case "startBuild":
          return { target: "cached", context };
        case "buildSucceeded":
          return { target: "cached", context };
        case "buildFailed":
          return { target: "cached", context };
        case "cacheHit":
          return { target: "cached", context };
        case "startRetry":
          return { target: "cached", context };
        case "cancelJob":
          return { target: "cached", context };
        case "builderStarted":
          return { target: "cached", context };
        default:
          return { target: state, context };
      }
    }
    case "succeeded": {
      switch (event) {
        case "startJobEval":
          return { target: "succeeded", context };
        case "jobEvalDone":
          return { target: "succeeded", context };
        case "startBuild":
          return { target: "succeeded", context };
        case "buildSucceeded":
          return { target: "succeeded", context };
        case "buildFailed":
          return { target: "succeeded", context };
        case "cacheHit":
          return { target: "succeeded", context };
        case "startRetry":
          return { target: "succeeded", context };
        case "cancelJob":
          return { target: "succeeded", context };
        case "builderStarted":
          return { target: "succeeded", context };
        default:
          return { target: state, context };
      }
    }
    case "failed": {
      switch (event) {
        case "startRetry":
          return { target: "retrying", context };
        case "cancelJob":
          return { target: "cancelled", context };
        case "startJobEval":
          return { target: "failed", context };
        case "jobEvalDone":
          return { target: "failed", context };
        case "startBuild":
          return { target: "failed", context };
        case "buildSucceeded":
          return { target: "failed", context };
        case "buildFailed":
          return { target: "failed", context };
        case "cacheHit":
          return { target: "failed", context };
        case "builderStarted":
          return { target: "failed", context };
        default:
          return { target: state, context };
      }
    }
    case "cancelled": {
      switch (event) {
        case "startJobEval":
          return { target: "cancelled", context };
        case "jobEvalDone":
          return { target: "cancelled", context };
        case "startBuild":
          return { target: "cancelled", context };
        case "buildSucceeded":
          return { target: "cancelled", context };
        case "buildFailed":
          return { target: "cancelled", context };
        case "cacheHit":
          return { target: "cancelled", context };
        case "startRetry":
          return { target: "cancelled", context };
        case "cancelJob":
          return { target: "cancelled", context };
        case "builderStarted":
          return { target: "cancelled", context };
        default:
          return { target: state, context };
      }
    }
    default:
      return { target: state, context };
  }
}

export function validTransitionJobStatus(from: JobStatus, event: JobEvent): boolean {
  switch (from) {
    case "queued": {
      switch (event) {
        case "startJobEval":
          return true;
        case "cacheHit":
          return true;
        case "cancelJob":
          return true;
        case "jobEvalDone":
          return true;
        case "startBuild":
          return true;
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "startRetry":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    case "evaluating": {
      switch (event) {
        case "jobEvalDone":
          return true;
        case "cancelJob":
          return true;
        case "startJobEval":
          return true;
        case "startBuild":
          return true;
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "cacheHit":
          return true;
        case "startRetry":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    case "preparing": {
      switch (event) {
        case "builderStarted":
          return true;
        case "buildFailed":
          return true;
        case "cancelJob":
          return true;
        case "startJobEval":
          return true;
        case "jobEvalDone":
          return true;
        case "startBuild":
          return true;
        case "buildSucceeded":
          return true;
        case "cacheHit":
          return true;
        case "startRetry":
          return true;
        default:
          return false;
      }
    }
    case "building": {
      switch (event) {
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "cancelJob":
          return true;
        case "startJobEval":
          return true;
        case "jobEvalDone":
          return true;
        case "startBuild":
          return true;
        case "cacheHit":
          return true;
        case "startRetry":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    case "retrying": {
      switch (event) {
        case "startBuild":
          return true;
        case "cancelJob":
          return true;
        case "startJobEval":
          return true;
        case "jobEvalDone":
          return true;
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "cacheHit":
          return true;
        case "startRetry":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    case "cached": {
      switch (event) {
        case "startJobEval":
          return true;
        case "jobEvalDone":
          return true;
        case "startBuild":
          return true;
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "cacheHit":
          return true;
        case "startRetry":
          return true;
        case "cancelJob":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    case "succeeded": {
      switch (event) {
        case "startJobEval":
          return true;
        case "jobEvalDone":
          return true;
        case "startBuild":
          return true;
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "cacheHit":
          return true;
        case "startRetry":
          return true;
        case "cancelJob":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    case "failed": {
      switch (event) {
        case "startRetry":
          return true;
        case "cancelJob":
          return true;
        case "startJobEval":
          return true;
        case "jobEvalDone":
          return true;
        case "startBuild":
          return true;
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "cacheHit":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    case "cancelled": {
      switch (event) {
        case "startJobEval":
          return true;
        case "jobEvalDone":
          return true;
        case "startBuild":
          return true;
        case "buildSucceeded":
          return true;
        case "buildFailed":
          return true;
        case "cacheHit":
          return true;
        case "startRetry":
          return true;
        case "cancelJob":
          return true;
        case "builderStarted":
          return true;
        default:
          return false;
      }
    }
    default:
      return false;
  }
}

export function terminalJobStatus(state: JobStatus): boolean {
  switch (state) {
    case "cached":
      return true;
    case "cancelled":
      return true;
    case "succeeded":
      return true;
    default:
      return false;
  }
}

//# sourceMappingURL=ci.ts.map
